package songwritersnotebook.app.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import songwritersnotebook.app.data.Song
import songwritersnotebook.app.data.GuitarTab
import songwritersnotebook.app.database.SongRepository
import songwritersnotebook.app.data.SongVersion
import songwritersnotebook.app.data.VersionType
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.TextFieldValue
import songwritersnotebook.app.data.*
import songwritersnotebook.app.billing.SubscriptionTier
import songwritersnotebook.app.utils.FractionalIndexer
import songwritersnotebook.app.utils.SongEditorUtils
import songwritersnotebook.app.utils.AudioHandler
import songwritersnotebook.app.utils.ChordUtils
import songwritersnotebook.app.utils.NetworkUtils
import songwritersnotebook.app.utils.WaveformGenerator
import songwritersnotebook.app.utils.CloudFunctionService
import songwritersnotebook.app.di.PlatformDependencyProvider
import kotlinx.datetime.Clock
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.*
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth


class EditorViewModel : ViewModel() {
    private val observerId = com.benasher44.uuid.uuid4().toString()
    private val dependencyProvider = PlatformDependencyProvider()
    
    private val songRepository: SongRepository? get() = dependencyProvider.getSongRepository()
    private val chordLibraryRepository: songwritersnotebook.app.database.ChordLibraryRepository? get() = dependencyProvider.getChordLibraryRepository()
    private val database: songwritersnotebook.database.AppDatabase? get() = dependencyProvider.getDatabase()
    
    private val httpClient = dependencyProvider.getHttpClient()
    private val preferencesManager = songwritersnotebook.app.utils.PreferencesManager()
    private val fileSystem = songwritersnotebook.platform.getPlatformFileSystem()
    private val networkUtils = songwritersnotebook.app.utils.NetworkUtils()
    private var preOfflineStatus: SaveStatus? = null
    
    val favoriteChords = chordLibraryRepository?.favoriteChords ?: kotlinx.coroutines.flow.MutableStateFlow(emptyList())
    
    private val aiChordService = songwritersnotebook.app.utils.AiChordService(httpClient)
    private val aiLyricService = songwritersnotebook.app.utils.AiLyricService(httpClient)
    private val aiHarmonyService = songwritersnotebook.app.utils.AiHarmonyService(httpClient)
    private val harmonyActionExecutor = songwritersnotebook.app.utils.HarmonyActionExecutor(this)
    private val thesaurusService = songwritersnotebook.app.utils.ThesaurusService(httpClient)
    
    private val audioHandler = dependencyProvider.getAudioHandler()
    private val ttsHelper = dependencyProvider.getTextToSpeech()
    
    private val chordRegex = """\[([^\]]+)\]""".toRegex()
    private var rhymeJob: kotlinx.coroutines.Job? = null
    
    private val _uiState = MutableStateFlow(EditorUiState(
        editorSettings = songwritersnotebook.app.viewmodels.EditorSettings(
            showSectionHeaders = preferencesManager.showSectionHeaders,
            chordDisplay = try { songwritersnotebook.app.viewmodels.ChordDisplayMode.valueOf(preferencesManager.chordDisplayMode) } catch(e: Exception) { songwritersnotebook.app.viewmodels.ChordDisplayMode.Large },
            syllableCountEnabled = preferencesManager.syllableCountEnabled,
            rhymingEnabled = preferencesManager.rhymingEnabled,
            showSlantRhymes = preferencesManager.showSlantRhymes,
            endOfLineOnly = preferencesManager.endOfLineOnly,
            showPianoInfo = false,
            showGuitarInfo = false,
            showTabs = preferencesManager.showTabs,
            isLeftHanded = preferencesManager.isLeftHanded,
            harmonyConfirmationRequired = preferencesManager.harmonyConfirmationRequired,
            harmonyVoiceEnabled = preferencesManager.harmonyVoiceEnabled
        ),
        deviceId = preferencesManager.deviceId
    ))
    val uiState: StateFlow<EditorUiState> = _uiState.asStateFlow()

    private data class TextDeltaUpdate(
        val songId: String,
        val sectionId: String,
        val lineId: String,
        val index: Int,
        val deleteCount: Int,
        val insertText: String,
        val isShared: Boolean
    )
    private val textUpdateChannel = kotlinx.coroutines.channels.Channel<TextDeltaUpdate>(kotlinx.coroutines.channels.Channel.UNLIMITED)

    private var debounceSaveJob: kotlinx.coroutines.Job? = null
    private var songObservationJob: kotlinx.coroutines.Job? = null
    private var presenceHeartbeatJob: kotlinx.coroutines.Job? = null
    private val DEBOUNCE_TIME_MS = 4000L
    private val hasUnsavedChanges = MutableStateFlow(false)
    private var lastKeystrokeTime = 0L
    private var hasSessionEdits = false
    private var lastPeriodicSaveTime = 0L
    private val PERIODIC_SAVE_INTERVAL_MS = 5 * 60 * 1000L
    private val currentSessionVersionId: String
        get() {
            val songId = _uiState.value.song?.id ?: return com.benasher44.uuid.uuid4().toString()
            val timeBlock = kotlinx.datetime.Clock.System.now().toEpochMilliseconds() / (1000 * 60 * 60) // 1-hour block
            return "${songId}_block_${timeBlock}"
        }

    private var bpmDebounceJob: kotlinx.coroutines.Job? = null

    private val deletedSectionIds = mutableListOf<String>()
    private val deletedLineIds = mutableListOf<String>()
    private val deletedTrackIds = mutableListOf<String>()
    private val deletedTrackItemIds = mutableListOf<String>()
    val deviceId = preferencesManager.deviceId
    val currentUserId: String? get() = songRepository?.getCurrentUserId()
    private val effectiveUserIdForChat: String get() = currentUserId ?: deviceId
    val sessionDeviceId = songRepository?.sessionDeviceId ?: com.benasher44.uuid.uuid4().toString()

    fun removeSession(deviceId: String) {
        viewModelScope.launch {
            songRepository?.removeSession(deviceId)
        }
    }

    private fun startPresenceHeartbeat(songId: String) {
        if (songId == songwritersnotebook.app.tutorial.TutorialManager.TUTORIAL_SONG_ID) return
        presenceHeartbeatJob?.cancel()
        presenceHeartbeatJob = viewModelScope.launch {
            while (isActive) {
                val state = _uiState.value
                val authUser = songwritersnotebook.app.utils.AuthHandler().getCurrentUser()
                val uid = currentUserId
                
                if (uid != null) {
                    val presence = songwritersnotebook.app.data.UserPresence(
                        userId = uid,
                        email = authUser?.email ?: state.userProfile?.email ?: "",
                        username = authUser?.displayName ?: state.userProfile?.username ?: "Songwriter",
                        deviceId = sessionDeviceId,
                        color = songwritersnotebook.app.data.UserPresence.generateUserColor(uid),
                        cursorPosition = state.cursorPosition,
                        activeSectionId = state.activeSectionId,
                        activeLineId = state.activeLineId,
                        lastActive = Clock.System.now().toEpochMilliseconds().toDouble()
                    )
                    songRepository?.updatePresence(songId, presence)
                }
                kotlinx.coroutines.delay(30000) // 30s heartbeat
            }
        }
    }

    fun updateCursorPosition(pos: Int, sectionId: String? = null, lineId: String? = null) {
        _uiState.update { it.copy(cursorPosition = pos, activeSectionId = sectionId, activeLineId = lineId) }
        if (isCollaborationEnabled || _uiState.value.presence.isNotEmpty()) {
            val songId = _uiState.value.song?.id ?: return
            val state = _uiState.value
            val authUser = songwritersnotebook.app.utils.AuthHandler().getCurrentUser()
            val presence = songwritersnotebook.app.data.UserPresence(
                userId = currentUserId ?: "anonymous",
                email = authUser?.email ?: state.userProfile?.email ?: "",
                username = authUser?.displayName ?: state.userProfile?.username ?: "Songwriter",
                deviceId = sessionDeviceId,
                color = songwritersnotebook.app.data.UserPresence.generateUserColor(currentUserId ?: "anonymous"),
                cursorPosition = pos,
                activeSectionId = sectionId,
                activeLineId = lineId,
                lastActive = Clock.System.now().toEpochMilliseconds().toDouble()
            )
            updatePresenceWithDebounce(presence)
        }
    }

    private val isShared: Boolean
        get() = _uiState.value.song?.let { it.ownerId.isNotEmpty() && it.ownerId != currentUserId } ?: false

    private val isCollaborationEnabled: Boolean
        get() = _uiState.value.song?.let { it.ownerId.isNotEmpty() && (it.collaborators.isNotEmpty() || it.viewers.isNotEmpty()) } == true

    private val isViewer: Boolean
        get() = _uiState.value.versionPreview != null || (_uiState.value.song?.viewers?.contains(currentUserId) == true && _uiState.value.song?.collaborators?.contains(currentUserId) != true)

    private var currentSubscriptionTier: SubscriptionTier = SubscriptionTier.FREE

    // --- Undo/Redo ---
    private var isApplyingUndoRedo = false

    private fun observeUndoRedoStatus() {
        viewModelScope.launch {
            songRepository?.canEditorUndo?.collect { can ->
                _uiState.update { it.copy(canUndo = can) }
            }
        }
        viewModelScope.launch {
            songRepository?.canEditorRedo?.collect { can ->
                _uiState.update { it.copy(canRedo = can) }
            }
        }
    }

    fun undo() {
        if (isViewer) return
        val currentSong = _uiState.value.song ?: return
        viewModelScope.launch {
            isApplyingUndoRedo = true
            hasUnsavedChanges.value = false // Clear local protection to allow adoption of the stack state
            songRepository?.undo(currentSong, songwritersnotebook.app.crdt.UndoScope.EDITOR)
            yield() // Give collector a chance to process the new flow value while isApplyingUndoRedo is still true
            isApplyingUndoRedo = false
        }
    }

    fun redo() {
        if (isViewer) return
        val currentSong = _uiState.value.song ?: return
        viewModelScope.launch {
            isApplyingUndoRedo = true
            hasUnsavedChanges.value = false // Clear local protection to allow adoption of the stack state
            songRepository?.redo(currentSong, songwritersnotebook.app.crdt.UndoScope.EDITOR)
            yield() // Give collector a chance to process the new flow value while isApplyingUndoRedo is still true
            isApplyingUndoRedo = false
        }
    }

    private val localEditsHistory = mutableListOf<String>()

    private fun applySongState(song: Song) {
        val currentSong = _uiState.value.song
        val activeSectionId = _uiState.value.activeSectionId
        val activeLineId = _uiState.value.activeLineId
        
        val finalSong = if (currentSong != null && activeSectionId != null && activeLineId != null) {
            var isEcho = false
            
            val serverSection = song.sections.find { it.id == activeSectionId }
            val serverLine = serverSection?.lines?.find { it.id == activeLineId }
            
            if (serverLine != null && localEditsHistory.contains(serverLine.text)) {
                isEcho = true
                val index = localEditsHistory.indexOf(serverLine.text)
                if (index >= 0) {
                    for (i in 0..index) {
                        localEditsHistory.removeAt(0)
                    }
                }
            } else if (serverLine != null) {
                localEditsHistory.clear()
            }

            if (isEcho) {
                val protectedSections = song.sections.map { section ->
                    if (section.id == activeSectionId) {
                        val currentSection = currentSong.sections.find { it.id == activeSectionId }
                        if (currentSection != null) {
                            val protectedLines = section.lines.map { line ->
                                if (line.id == activeLineId) {
                                    val currentLine = currentSection.lines.find { it.id == activeLineId }
                                    currentLine ?: line
                                } else line
                            }
                            section.copy(lines = protectedLines)
                        } else section
                    } else section
                }
                song.copy(sections = protectedSections)
            } else {
                song
            }
        } else {
            song
        }

        if (currentSong != null) {
            // Diffing for granular undo/redo saves
            // 1. Check sections
            if (finalSong.sections.size != currentSong.sections.size) {
                // If structure changed (added/removed sections), we can't easily pinpoint a single section.
                // However, undo usually involves single actions.
                // For simplicity, if size differs, mark all or let it fall back.
                // But better: mark all current indices as modified.
                (0 until maxOf(finalSong.sections.size, currentSong.sections.size)).forEach { modifiedSectionIndices.add(it) }
            } else {
                finalSong.sections.forEachIndexed { index, section ->
                    if (section != currentSong.sections[index]) {
                        modifiedSectionIndices.add(index)
                    }
                }
            }

            // 2. Check metadata
            if (finalSong.title != currentSong.title ||
                finalSong.bpm != currentSong.bpm ||
                finalSong.guitarTuning != currentSong.guitarTuning ||
                finalSong.guitarCapo != currentSong.guitarCapo ||
                finalSong.notes != currentSong.notes ||
                finalSong.showNotes != currentSong.showNotes ||
                finalSong.showPianoInfo != currentSong.showPianoInfo ||
                finalSong.showGuitarInfo != currentSong.showGuitarInfo) {
                modifiedMetadata = true
            }

            // 3. Check Chord Library
            if (finalSong.chordDefinitions != currentSong.chordDefinitions) {
                modifiedChordLibrary = true
            }
        }

        val chordsInUse = getChordsInSong(finalSong)
        val orderedChords = songwritersnotebook.app.utils.ChordUtils.getOrderedChordsInSong(finalSong)
        
        _uiState.update { 
            it.copy(
                song = finalSong, 
                chordsInUse = chordsInUse, 
                orderedChordNames = orderedChords,
                guitarTuning = finalSong.guitarTuning,
                guitarCapo = finalSong.guitarCapo
            ) 
        }
        findRhymes(finalSong, _uiState.value.editorSettings)
    }

    init {
        println("DEBUG_DIAG: EditorViewModel initialized")
        database?.let { songwritersnotebook.app.data.RhymeRepository.initialize(it) }
        observeUndoRedoStatus()
        songRepository?.registerObserver(observerId)

        viewModelScope.launch(songwritersnotebook.app.AppDispatchers.IO) {
            for (update in textUpdateChannel) {
                songRepository?.pushLineTextUpdate(
                    songId = update.songId,
                    sectionId = update.sectionId,
                    lineId = update.lineId,
                    index = update.index,
                    deleteCount = update.deleteCount,
                    insertText = update.insertText,
                    isShared = update.isShared
                )
            }
        }
        
        // Monitor Network Status
        viewModelScope.launch {
            networkUtils.networkStatus
                .debounce { online: Boolean -> if (online) 0L else 2000L }
                .collect { isOnline: Boolean ->
                _uiState.update { state ->
                    if (!isOnline) {
                        if (state.saveStatus != SaveStatus.OFFLINE) {
                            preOfflineStatus = state.saveStatus
                        }
                        state.copy(saveStatus = SaveStatus.OFFLINE)
                    } else {
                        val restoredStatus = preOfflineStatus ?: state.saveStatus
                        // If we were offline, return to IDLE or SYNCING (if there were unsaved changes)
                        val nextStatus = if (state.saveStatus == SaveStatus.OFFLINE) {
                            if (hasUnsavedChanges.value) SaveStatus.SYNCING else SaveStatus.IDLE
                        } else {
                            state.saveStatus
                        }
                        state.copy(saveStatus = nextStatus)
                    }
                }
            }
        }
        viewModelScope.launch {
            chordLibraryRepository?.favoriteProgressions?.collect { favProgs ->
                _uiState.update { it.copy(favoriteProgressions = favProgs) }
            }
        }

        viewModelScope.launch {
            songRepository?.uploadStatus?.collect { statusMap ->
                _uiState.update { it.copy(uploadStatus = statusMap) }
            }
        }

        viewModelScope.launch {
            songRepository?.currentUserProfile?.collect { profile ->
                setUserProfile(profile)
            }
        }

        // Sync Notifications
        viewModelScope.launch {
            songRepository?.syncNotifications?.collect { message ->
                songwritersnotebook.app.utils.PlatformToast.show(message)
            }
        }
    }

    fun setUserProfile(profile: songwritersnotebook.app.data.UserProfile?) {
        val previousUid = _uiState.value.userProfile?.uid
        _uiState.update { it.copy(userProfile = profile) }
        
        val newUid = profile?.uid
        val songId = _uiState.value.song?.id
        
        println("DEBUG_HARMONY_SYNC: setUserProfile called. newUid=\$newUid, previousUid=\$previousUid, songId=\$songId")
        
        if (songId != null && previousUid != newUid) {
            viewModelScope.launch {
                val effectiveUid = effectiveUserIdForChat
                println("DEBUG_HARMONY_SYNC: Fetching history for effectiveUid=\$effectiveUid in setUserProfile")
                val userHistory = songRepository?.getHarmonyChatHistory(songId, effectiveUid, limit = 50)?.reversed() ?: emptyList()
                println("DEBUG_HARMONY_SYNC: Found \${userHistory.size} messages for effectiveUid=\$effectiveUid")
                if (userHistory.isNotEmpty()) {
                    _uiState.update { it.copy(harmonyChatHistory = userHistory.map { msg -> HarmonyChatMessage(text = msg.text, isUser = msg.isUser, id = msg.id) }) }
                }
            }
        }
    }

    fun updateSubscriptionTier(tier: SubscriptionTier) {
        currentSubscriptionTier = tier
    }

    fun loadSongById(songId: String, subscriptionTier: SubscriptionTier = SubscriptionTier.FREE) {
        if (songId.isBlank()) return

        // CRITICAL: Stop previous observation and clean up repository session
        val previousSongId = _uiState.value.song?.id
        if (previousSongId != null) {
            songRepository?.stopSongObservation(previousSongId, currentUserId, observerId)
        }

        songObservationJob?.cancel()
        checkpointJob?.cancel()
        presenceHeartbeatJob?.cancel()
        debounceSaveJob?.cancel() // Cancel any pending saves for the OLD song

        currentSubscriptionTier = subscriptionTier
        hasUnsavedChanges.value = false
        hasSessionEdits = false
        _uiState.update { it.copy(saveStatus = SaveStatus.IDLE, song = null) }
        
        startPresenceHeartbeat(songId)
        
        // Monitor Sync Status (Pro Sync feedback)
        viewModelScope.launch {
            if (songId == songwritersnotebook.app.tutorial.TutorialManager.TUTORIAL_SONG_ID) return@launch
            
            combine(
                songRepository?.pendingDeltasCount ?: MutableStateFlow(0),
                songRepository?.getBakeStatus(songId) ?: flowOf("idle"),
                hasUnsavedChanges
            ) { pending: Int, bake: String?, unsaved: Boolean ->
                Triple(pending, bake, unsaved)
            }.collect { (pending, bake, unsaved) ->
                _uiState.update { state ->
                    val isBaking = bake == "pending"
                    val isSyncing = pending > 0
                    
                    if (!isSyncing && !isBaking && state.saveStatus == SaveStatus.SYNCING) {
                        // Sync finished, but we no longer reset hasUnsavedChanges here.
                        // We keep it true to protect the local session state from 
                        // being overwritten by incoming cloud updates until a formal 
                        // performSave() checkpoint is completed.
                    }
                    val currentUnsaved = hasUnsavedChanges.value
                    
                    val newStatus = when {
                        state.isSaving -> SaveStatus.SAVING // Priority for actual network save
                        isSyncing || isBaking -> SaveStatus.SYNCING // Show CloudUpload while deltas are pending
                        state.saveStatus == SaveStatus.ERROR -> SaveStatus.ERROR // Keep error until next attempt
                        state.saveStatus == SaveStatus.SAVING || state.saveStatus == SaveStatus.SYNCING -> {
                            // Sync/Save just finished
                            SaveStatus.SAVED
                        }
                        else -> state.saveStatus
                    }
                    
                    // Auto-hide "Saved" after a delay
                    if (newStatus == SaveStatus.SAVED && state.saveStatus != SaveStatus.SAVED) {
                        viewModelScope.launch {
                            delay(3000)
                            _uiState.update { if (it.saveStatus == SaveStatus.SAVED && !hasUnsavedChanges.value) it.copy(saveStatus = SaveStatus.IDLE) else it }
                        }
                    }
                    
                    state.copy(saveStatus = newStatus)
                }
            }
        }

        songObservationJob = viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true) }
            
            launch {
                songRepository?.getVersions(songId)?.collect { list ->
                    _uiState.update { it.copy(versions = list) }
                }
            }
            // --- HARMONY INTRO & HISTORY ---
            val repo = songRepository
            val initialSong = repo?.getSongById(songId)
            val effectiveUserIdForLoad = effectiveUserIdForChat
            
            println("DEBUG_HARMONY_SYNC: loadSongById fetching history for effectiveUserIdForLoad=\$effectiveUserIdForLoad")
            
            if (initialSong != null && repo != null) {
                // Fetch paginated history from subcollection/local DB
                val userHistory = repo.getHarmonyChatHistory(songId, effectiveUserIdForLoad, limit = 50).reversed()
                println("DEBUG_HARMONY_SYNC: loadSongById found \${userHistory.size} messages")
                
                if (userHistory.isEmpty()) {
                    val intro = songwritersnotebook.app.data.HarmonyChatMessage(
                        text = "Hi! I'm Harmony, your songwriting assistant. I can help you find rhymes, suggest chords, rename sections, or even change your guitar tuning.\n\nTry asking me things like:\n- \"What's a good rhyme for 'sky'?\"\n- \"Suggest a chord progression for a sad verse.\"\n- \"Can you change the tuning to Drop D?\"\n\nJust ask or hold the mic!",
                        isUser = false,
                        userId = effectiveUserIdForLoad
                    )
                    repo.saveHarmonyChatMessage(songId, intro)
                    val introUi = HarmonyChatMessage(text = intro.text, isUser = intro.isUser, id = intro.id)
                    _uiState.update { it.copy(harmonyChatHistory = listOf(introUi)) }
                } else {
                    _uiState.update { it.copy(harmonyChatHistory = userHistory.map { HarmonyChatMessage(text = it.text, isUser = it.isUser, id = it.id) }) }
                }
            }

            songRepository?.observeSong(songId)?.collect { song ->
                if (song != null && !song.isDeleted) {
                    println("DEBUG_VM: Song $songId loaded successfully. isDeleted=${song.isDeleted}")
                    _uiState.update { it.copy(shouldExit = false) } // Reset if we have access
                    handleSongUpdate(song)
                    startCheckpointTimer()

                    // Sync Audio
                    val repo = songRepository
                    if (repo != null) {
                        launch(songwritersnotebook.app.AppDispatchers.IO) {
                            repo.syncRecordingsForSong(song) { msg ->
                                _uiState.update { it.copy(audioSyncProgress = if (msg.isNotBlank()) 0f else null) }
                            }
                        }
                    }
                } else {
                    // ACCESS REVOKED: Song became null while we were editing
                    if (song == null) {
                        println("DEBUG_VM: Access revoked for $songId during active edit because song flow emitted NULL.")
                    } else if (song.isDeleted) {
                        println("DEBUG_VM: Access revoked for $songId during active edit because song.isDeleted is TRUE.")
                    }
                    _uiState.update { it.copy(shouldExit = true) }
                    
                    // Refresh the global repository so the Home screen reflects the removed access
                    viewModelScope.launch {
                        songRepository?.syncSongsWithFirestore {}
                    }
                }
                _uiState.update { it.copy(isLoading = false) }
            }

        }
    }

    private var checkpointJob: kotlinx.coroutines.Job? = null
    private fun startCheckpointTimer() {
        if (checkpointJob?.isActive == true) return
        checkpointJob = viewModelScope.launch {
            while (isActive) {
                delay(5 * 60 * 1000L) // 5 minutes
                val song = _uiState.value.song
                if (song != null && hasSessionEdits) {
                    println("DEBUG_VM: Saving periodic checkpoint")
                    // 1. Perform full Firestore save
                    performSave()
                    // 2. Save local history version
                    songRepository?.saveVersionNonBlocking(song, VersionType.CHECKPOINT, "Autosaved Session Checkpoint", currentSessionVersionId)
                    // 3. Reset session flag so we don't save again unless new changes happen
                    hasSessionEdits = false
                }
            }
        }
    }

    fun loadSong(song: Song) {
        handleSongUpdate(song)
    }    
    private fun handleSongUpdate(song: Song) {
        val now = Clock.System.now().toEpochMilliseconds()
        
        // 1. Filter Remote Presence
        val filteredPresence = song.presence.filter { entry ->
            val presence = entry.value
            val isNotSelf = presence.deviceId != sessionDeviceId
            val isRecent = (now - presence.lastActive) < 60000
            isNotSelf && isRecent
        }

        var isInitialLoad = false

        // 2. ATOMIC MERGE & STABILITY CHECK
        _uiState.update { currentState ->
            val uid = currentUserId
            
            // PRO SYNC: Trigger rhymes if this is the first time we see the song, 
            // OR if we are transitioning from an empty shell (no sections) to a populated song.
            isInitialLoad = currentState.song == null || (currentState.song?.sections?.isEmpty() == true && song.sections.isNotEmpty())
            
            val isShell = song.sections.isEmpty() && (song.studioState?.tracks?.isEmpty() ?: true)
            val isCloudSong = song.ownerId.isNotEmpty() && song.ownerId != uid
            val isStudioUser = currentSubscriptionTier == SubscriptionTier.STUDIO || currentSubscriptionTier == SubscriptionTier.UNKNOWN
            
            val localSong = currentState.song ?: return@update currentState.copy(
                song = song,
                presence = filteredPresence,
                // PRO FIX: Stay in loading state if this is a blank shell from the cloud.
                // We only stop loading once we have real sections or the cloud sync finishes.
                isLoading = isShell && (isCloudSong || isCollaborationEnabled || isStudioUser)
            )

            val isRichContentAvailable = song.sections.isNotEmpty() || (song.studioState?.tracks?.isNotEmpty() ?: false)
            val finalIsLoading = if (isRichContentAvailable) false else currentState.isLoading
            
            val isOwner = uid != null && song.ownerId == uid
            val isCollaborator = uid != null && song.collaborators.contains(uid)
            val isActuallyViewer = uid != null && !isOwner && !isCollaborator

            songwritersnotebook.app.utils.AppLogger.d("EditorViewModel", "DEBUG_ACCESS: songId=${song.id}, uid=$uid, ownerId=${song.ownerId}, isOwner=$isOwner, isCollab=$isCollaborator, collaborators=${song.collaborators}, isViewer=$isActuallyViewer")

            val isCleaningUp = isApplyingUndoRedo
            var finalSong = if (!isCleaningUp) {
                // PRO CHORD MERGE: Keep locally added chords that haven't hit the cloud yet.
                val combinedChords = song.chordDefinitions.toMutableMap()
                localSong.chordDefinitions.forEach { (name, def) ->
                    if (!combinedChords.containsKey(name)) {
                        combinedChords[name] = def
                    }
                }

                // Filter out locally deleted items from incoming updates
                val filteredSections = song.sections
                    .filter { it.id !in deletedSectionIds }
                    .map { section ->
                        val localSection = localSong.sections.find { it.id == section.id }
                        val filteredLines = section.lines.filter { it.id !in deletedLineIds }
                        
                        section.copy(
                            lines = filteredLines
                        )
                    }
                
                song.copy(
                    sections = filteredSections,
                    chordDefinitions = combinedChords
                )
            } else {
                song
            }

            // PRO SYNC: Protect active editing line from delayed cloud echoes
            val activeSectionId = currentState.activeSectionId
            val activeLineId = currentState.activeLineId
            if (activeSectionId != null && activeLineId != null) {
                var isEcho = false
                val serverSection = finalSong.sections.find { it.id == activeSectionId }
                val serverLine = serverSection?.lines?.find { it.id == activeLineId }
                
                if (serverLine != null && localEditsHistory.contains(serverLine.text)) {
                    isEcho = true
                }

                if (isEcho) {
                    val protectedSections = finalSong.sections.map { section ->
                        if (section.id == activeSectionId) {
                            val currentSection = localSong.sections.find { it.id == activeSectionId }
                            if (currentSection != null) {
                                val protectedLines = section.lines.map { line ->
                                    if (line.id == activeLineId) {
                                        val currentLine = currentSection.lines.find { it.id == activeLineId }
                                        currentLine ?: line
                                    } else line
                                }
                                section.copy(lines = protectedLines)
                            } else section
                        } else section
                    }
                    finalSong = finalSong.copy(sections = protectedSections)
                }
            }

            val mergedSong = finalSong

            // PRO STABILITY: We only refresh the UI if the actual CONTENT changed.
            // This ignores local echoes (feedback loop) and metadata changes like timestamps.
            val isContentIdentical = isSongContentEquivalent(mergedSong, localSong)

            if (isContentIdentical && 
                filteredPresence == currentState.presence && 
                isActuallyViewer == currentState.isViewer) {
                // Return exactly the same instance to prevent Compose from re-evaluating the TextField
                return@update currentState
            }

            val chordsChanged = !isChordLibraryEquivalent(mergedSong.chordDefinitions, localSong.chordDefinitions) ||
                                !isSectionListEquivalent(mergedSong.sections, localSong.sections)

            val effectiveIdForUpdate = uid ?: sessionDeviceId
            currentState.copy(
                song = mergedSong,
                presence = filteredPresence,
                isViewer = isActuallyViewer,
                isShared = mergedSong.ownerId.isNotEmpty() && mergedSong.ownerId != uid,
                isLoading = false,
                orderedChordNames = if (chordsChanged) songwritersnotebook.app.utils.ChordUtils.getOrderedChordsInSong(mergedSong) else currentState.orderedChordNames,
                chordsInUse = if (chordsChanged) getChordsInSong(mergedSong) else currentState.chordsInUse,
                guitarTuning = mergedSong.guitarTuning,
                guitarCapo = mergedSong.guitarCapo,
                editorSettings = currentState.editorSettings.copy(
                    showPianoInfo = preferencesManager.getShowPianoInfo(mergedSong.id),
                    showGuitarInfo = preferencesManager.getShowGuitarInfo(mergedSong.id)
                )
            )
        }
        
        // 3. Finalize Sync Status
        val finalState = _uiState.value
        val finalSong = finalState.song
        if (!isApplyingUndoRedo && finalSong != null) {
            if (!hasUnsavedChanges.value || isSectionListEquivalent(finalSong.sections, song.sections)) {
                hasUnsavedChanges.value = false
            }
        }
        
        // PRO RHYME TRIGGER: Trigger if this is the initial load OR if we finally have content but no rhymes yet.
        // This handles the "blank shell" race condition where cloud data arrives in stages.
        val hasTextContent = finalSong?.sections?.any { it.lines.any { l -> l.text.isNotBlank() } } == true
        val needsRhymes = finalState.rhymeData.isEmpty() && hasTextContent && finalState.editorSettings.rhymingEnabled
        
        if ((isInitialLoad || needsRhymes) && finalSong != null) {
            findRhymes(finalSong, finalState.editorSettings, immediate = true)
        }
        
        // NO RHYME CALCULATION HERE: Rhymes are only triggered by local input 
        // to prevent flickering during typing bursts.
    }

    private fun isSongContentEquivalent(s1: Song, s2: Song): Boolean {
        return s1.title == s2.title &&
               s1.notes == s2.notes &&
               s1.bpm == s2.bpm &&
               s1.guitarTuning == s2.guitarTuning &&
               s1.guitarCapo == s2.guitarCapo &&
               s1.showNotes == s2.showNotes &&
               s1.showPianoInfo == s2.showPianoInfo &&
               s1.showGuitarInfo == s2.showGuitarInfo &&
               isChordLibraryEquivalent(s1.chordDefinitions, s2.chordDefinitions) &&
               isSectionListEquivalent(s1.sections, s2.sections) &&
               s1.recordings.keys == s2.recordings.keys // Simplified check for recordings
    }

    private fun isSectionListEquivalent(l1: List<Section>, l2: List<Section>): Boolean {
        if (l1.size != l2.size) return false
        return l1.indices.all { i ->
            val s1 = l1[i]
            val s2 = l2[i]
            s1.id == s2.id && s1.title == s2.title && s1.position == s2.position &&
            isLineListEquivalent(s1.lines, s2.lines)
        }
    }

    private fun isLineListEquivalent(l1: List<Line>, l2: List<Line>): Boolean {
        if (l1.size != l2.size) return false
        return l1.indices.all { i ->
            val line1 = l1[i]
            val line2 = l2[i]
            line1.id == line2.id && line1.text == line2.text && line1.position == line2.position
        }
    }

    private fun isChordLibraryEquivalent(m1: Map<String, ChordDefinition>, m2: Map<String, ChordDefinition>): Boolean {
        if (m1.size != m2.size) return false
        return m1.all { (k, v) ->
            val v2 = m2[k] ?: return false
            v.name == v2.name && v.root == v2.root && v.quality == v2.quality &&
            v.pianoNotes == v2.pianoNotes && v.guitarTab == v2.guitarTab
        }
    }

    private suspend fun processSongUpdate(song: Song) {
        findRhymes(song, _uiState.value.editorSettings, immediate = true)
    }

    private val modifiedSectionIndices = mutableSetOf<Int>()
    private var modifiedMetadata = false
    private var modifiedChordLibrary = false
    private var modifiedStructurally = false

    fun updateSong(
        song: Song, 
        sectionIndex: Int? = null,
        metadataChanged: Boolean = false,
        chordLibraryChanged: Boolean = false,
        deletedSectionId: String? = null,
        deletedLineId: String? = null,
        forceUndoStep: Boolean = false,
        pushToDatabase: Boolean = true
    ) {
        if (isViewer) return
        
        deletedSectionId?.let { deletedSectionIds.add(it) }
        deletedLineId?.let { deletedLineIds.add(it) }

        val updatedSong = song.copy(lastModified = Clock.System.now())
        
        // PRO OPTIMIZATION: Immediate UI update only for the song object.
        // We defer heavy chord/rhyme calculations to a debounced background job
        // to prevent input stuttering during rapid edits.
        _uiState.update { it.copy(song = updatedSong) }
        
        if (sectionIndex != null) {
            modifiedSectionIndices.add(sectionIndex)
        } else if (!metadataChanged && !chordLibraryChanged) {
            modifiedStructurally = true
        }
        
        if (metadataChanged) modifiedMetadata = true
        if (chordLibraryChanged) modifiedChordLibrary = true
        
        triggerDebouncedSave()
        
        // Trigger lazy secondary updates (chords, rhymes)
        triggerSecondaryUpdates(updatedSong)
        
        hasUnsavedChanges.value = true
        hasSessionEdits = true
        lastKeystrokeTime = Clock.System.now().toEpochMilliseconds()
        
        val shouldForce = forceUndoStep || sectionIndex == null || metadataChanged || chordLibraryChanged || deletedSectionId != null || deletedLineId != null
        
        if (pushToDatabase) {
            viewModelScope.launch(Dispatchers.Default) {
                songRepository?.pushCrdtUpdate(updatedSong.id, updatedSong, scope = songwritersnotebook.app.crdt.UndoScope.EDITOR, forceUndoStep = shouldForce)
            }
        }
    }

    private var secondaryUpdateJob: Job? = null
    
    /**
     * Handles heavy calculations like chord indexing and rhyme finding.
     * Debounced to ensure rapid typing stays smooth.
     */
    private fun triggerSecondaryUpdates(song: Song) {
        secondaryUpdateJob?.cancel()
        secondaryUpdateJob = viewModelScope.launch(Dispatchers.Default) {
            // Short delay to let rapid keystrokes pass
            delay(300)
            
            val chordsInUse = getChordsInSong(song)
            val orderedChords = songwritersnotebook.app.utils.ChordUtils.getOrderedChordsInSong(song)
            
            withContext(Dispatchers.Main) {
                _uiState.update { 
                    it.copy(
                        chordsInUse = chordsInUse, 
                        orderedChordNames = orderedChords,
                        guitarTuning = song.guitarTuning,
                        guitarCapo = song.guitarCapo
                    ) 
                }
                findRhymes(song, _uiState.value.editorSettings, immediate = true)
            }
        }
    }
    
    private fun getChordsInSong(song: Song): Set<String> {
        val chords = mutableSetOf<String>()
        song.sections.forEach { section ->
            section.lines.forEach { line ->
                chordRegex.findAll(line.text).forEach { match ->
                    if (song.chordDefinitions.containsKey(match.groupValues[1])) {
                        chords.add(match.groupValues[1])
                    }
                }
            }
        }
        return chords
    }

    fun saveSong() {
        performSave()
    }

    private fun performSave() {
        if (isViewer) {
            println("DEBUG_VM: performSave aborted - user is viewer")
            return
        }

         _uiState.update { it.copy(isSaving = true, saveStatus = SaveStatus.SAVING) }
         val songBeingSaved = _uiState.value.song
         
         if (songBeingSaved != null) {
             println("DEBUG_VM: performSave starting for ${songBeingSaved.title} (${songBeingSaved.id})")
             val onComplete: (SaveSongResponse?) -> Unit = { response ->
                 viewModelScope.launch {
                     if (response?.status == "success") {
                        println("DEBUG_VM: Save completed SUCCESSFULLY for ${songBeingSaved.title}. New Revision: ${response.revisionId}")
                        
                        // Reset tracking ONLY after a successful server confirmation
                        deletedLineIds.clear()
                        deletedSectionIds.clear()
                        deletedTrackIds.clear()
                        deletedTrackItemIds.clear()
                        modifiedSectionIndices.clear()
                        modifiedMetadata = false
                        modifiedChordLibrary = false
                        modifiedStructurally = false

                        val currentSongInState = _uiState.value.song
                        if (currentSongInState?.id == songBeingSaved.id) {
                            // DEEP CONTENT CHECK: Ensure no new edits happened during the network turn
                            val isContentIdentical = currentSongInState.sections == songBeingSaved.sections &&
                                                     currentSongInState.studioState == songBeingSaved.studioState &&
                                                     currentSongInState.chordDefinitions == songBeingSaved.chordDefinitions &&
                                                     currentSongInState.title == songBeingSaved.title &&
                                                     currentSongInState.bpm == songBeingSaved.bpm &&
                                                     currentSongInState.guitarTuning == songBeingSaved.guitarTuning &&
                                                     currentSongInState.guitarCapo == songBeingSaved.guitarCapo &&
                                                     currentSongInState.notes == songBeingSaved.notes

                            if (isContentIdentical) {
                                // PRO GUARD: We only lower the protection shield if we are sure 
                                // no new keystrokes have happened and the cloud matches our intent.
                                println("DEBUG_VM: Content identical, marking as saved.")
                                hasUnsavedChanges.value = false
                                _uiState.update { state -> 
                                    state.copy(
                                        saveStatus = SaveStatus.SAVED,
                                        song = state.song?.copy(revisionId = response.revisionId ?: state.song.revisionId)
                                    ) 
                                }
                            } else {
                                println("DEBUG_VM: New changes detected during save network request. Keeping hasUnsavedChanges=true")
                            }
                        }
                        
                        _uiState.update { it.copy(isSaving = false) }
                        kotlinx.coroutines.delay(2000L)
                        _uiState.update { if (it.saveStatus == SaveStatus.SAVED && !hasUnsavedChanges.value) it.copy(saveStatus = SaveStatus.IDLE) else it }
                     } else {
                         println("DEBUG_VM: Save FAILED for ${songBeingSaved.title}: ${response?.message}")
                         _uiState.update { it.copy(saveStatus = SaveStatus.ERROR, isSaving = false) }
                     }
                 }
             }

             // Unified Save Strategy: Always use the full-doc checkpoint save
             // which now handles sub-collection syncing and captures the CRDT snapshot.
             println("DEBUG_VM: Using saveSongToAllSources strategy (Structural: $modifiedStructurally, Metadata: $modifiedMetadata, Sections: ${modifiedSectionIndices.size})")
             songRepository?.saveSongToAllSourcesNonBlocking(
                 song = songBeingSaved,
                 scope = songwritersnotebook.app.crdt.UndoScope.EDITOR,
                 deletedLineIds = deletedLineIds.toList(),
                 deletedSectionIds = deletedSectionIds.toList(),
                 deletedTrackIds = deletedTrackIds.toList(),
                 deletedTrackItemIds = deletedTrackItemIds.toList(),
                 onComplete = onComplete
             )
         } else {
             println("DEBUG_VM: performSave aborted - song is null")
             _uiState.update { it.copy(isSaving = false) }
         }
    }

    private fun triggerDebouncedSave() {
        println("DEBUG_VM: triggerDebouncedSave called. hasUnsavedChanges.value=$hasUnsavedChanges.value")
        debounceSaveJob?.cancel()
        
        // PRO OPTIMIZATION: Do not perform full document HTTP saves on every keystroke for Cloud Songs.
        // Cloud Songs rely on the Master Baker (SongCrdtBakeTrigger) to merge RTDB atomic deltas every 5 seconds.
        // This eliminates Firestore write amplification and massive read loops.
        val isCloudSong = _uiState.value.song?.ownerId?.isNotEmpty() == true
        if (isCloudSong) {
            println("DEBUG_VM: Skipping triggerDebouncedSave for Cloud Song. Relying on CRDT + Master Baker.")
            return
        }
        
        debounceSaveJob = viewModelScope.launch {
            kotlinx.coroutines.delay(DEBOUNCE_TIME_MS)
            println("DEBUG_VM: Debounce delay finished, calling performSave()")
            performSave()
        }
    }

    // --- UI Binding ---
    fun updateSongTitle(title: String) {
        if (isViewer) return
        _uiState.value.song?.let { song ->
            val oldText = song.title
            val newText = title
            if (oldText == newText) return@let

            // PRO COLLABORATION: Atomic Diffing
            var prefixLen = 0
            while (prefixLen < oldText.length && prefixLen < newText.length && oldText[prefixLen] == newText[prefixLen]) {
                prefixLen++
            }

            var suffixLen = 0
            while (suffixLen < oldText.length - prefixLen && 
                   suffixLen < newText.length - prefixLen && 
                   oldText[oldText.length - 1 - suffixLen] == newText[newText.length - 1 - suffixLen]) {
                suffixLen++
            }

            val deleteCount = oldText.length - prefixLen - suffixLen
            val insertText = newText.substring(prefixLen, newText.length - suffixLen)

            // 1. Update Local UI immediately AND synchronously (Main Thread)
            val updatedSong = song.copy(title = title, lastModified = Clock.System.now())
            _uiState.update { it.copy(song = updatedSong) }
            hasUnsavedChanges.value = true
            hasSessionEdits = true

            // 2. Push Atomic Delta in background
            viewModelScope.launch(Dispatchers.Default) {
                songRepository?.pushSongTitleUpdate(
                    songId = song.id,
                    index = prefixLen,
                    deleteCount = deleteCount,
                    insertText = insertText,
                    isShared = isCollaborationEnabled
                )
            }
            triggerDebouncedSave()
            lastKeystrokeTime = Clock.System.now().toEpochMilliseconds()
        }
    }

    fun updateSectionTitle(sectionIndex: Int, newTitle: String) {
        if (isViewer) return
        _uiState.value.song?.let { currentSong ->
            val currentSection = currentSong.sections.getOrNull(sectionIndex) ?: return@let
            val oldText = currentSection.title
            val newText = newTitle
            if (oldText == newText) return@let

            // PRO COLLABORATION: Atomic Diffing
            var prefixLen = 0
            while (prefixLen < oldText.length && prefixLen < newText.length && oldText[prefixLen] == newText[prefixLen]) {
                prefixLen++
            }

            var suffixLen = 0
            while (suffixLen < oldText.length - prefixLen && 
                   suffixLen < newText.length - prefixLen && 
                   oldText[oldText.length - 1 - suffixLen] == newText[newText.length - 1 - suffixLen]) {
                suffixLen++
            }

            val deleteCount = oldText.length - prefixLen - suffixLen
            val insertText = newText.substring(prefixLen, newText.length - suffixLen)

            // 1. Update Local UI immediately AND synchronously (Main Thread)
            val updatedSection = currentSection.copy(title = newTitle, lastModified = Clock.System.now().toEpochMilliseconds())
            val newSections = currentSong.sections.toMutableList()
            newSections[sectionIndex] = updatedSection
            val updatedSong = currentSong.copy(sections = newSections, lastModified = Clock.System.now())
            _uiState.update { it.copy(song = updatedSong) }
            hasUnsavedChanges.value = true
            hasSessionEdits = true

            // 2. Push Atomic Delta in background
            viewModelScope.launch(Dispatchers.Default) {
                songRepository?.pushSectionTitleUpdate(
                    songId = currentSong.id,
                    userId = currentUserId ?: "",
                    sectionId = currentSection.id,
                    index = prefixLen,
                    deleteCount = deleteCount,
                    insertText = insertText,
                    isShared = isCollaborationEnabled
                )
            }
            triggerDebouncedSave()
            lastKeystrokeTime = Clock.System.now().toEpochMilliseconds()
        }
    }

    private fun getUserColor(userId: String): Int {
        val profile = _uiState.value.userProfile
        if (profile?.color != null) return profile.color!!
        
        val generated = songwritersnotebook.app.data.UserPresence.generateUserColor(userId)
        // Persist color in profile if not set
        if (profile != null && profile.color == null) {
            val updatedProfile = profile.copy(color = generated)
            viewModelScope.launch(songwritersnotebook.app.AppDispatchers.IO) {
                songRepository?.updateUserProfile(updatedProfile)
            }
        }
        return generated
    }

    private var presenceUpdateJob: Job? = null

    private fun updatePresenceWithDebounce(presence: songwritersnotebook.app.data.UserPresence) {
        if (!isCollaborationEnabled && _uiState.value.presence.isEmpty()) return
        val songId = _uiState.value.song?.id ?: return

        presenceUpdateJob?.cancel()
        presenceUpdateJob = viewModelScope.launch(songwritersnotebook.app.AppDispatchers.IO) {
            delay(2000) // 2000ms debounce for cursor/presence to prevent spamming RTDB
            songRepository?.updatePresence(songId, presence)
        }
    }

    fun updateLineText(sectionIndex: Int, lineIndex: Int, newTextFieldValue: TextFieldValue) {
        if (isViewer) return
        val text = newTextFieldValue.text
        
        val song = _uiState.value.song ?: return
        val section = song.sections.getOrNull(sectionIndex) ?: return
        val line = section.lines.getOrNull(lineIndex) ?: return

        // QUARANTINE: Mark this specific line as active to prevent cursor jumps

        // Sync cursor position for Live Cursors
        if (isCollaborationEnabled || _uiState.value.presence.isNotEmpty()) {
            val userId = currentUserId
            if (userId != null) {
                val userColor = getUserColor(userId)
                val authUser = songwritersnotebook.app.utils.AuthHandler().getCurrentUser()
                val username = authUser?.displayName ?: _uiState.value.userProfile?.username ?: "Songwriter"
                val email = authUser?.email ?: _uiState.value.userProfile?.email ?: ""
                val songId = _uiState.value.song?.id
                val sectionId = _uiState.value.song?.sections?.getOrNull(sectionIndex)?.id
                val lineId = _uiState.value.song?.sections?.getOrNull(sectionIndex)?.lines?.getOrNull(lineIndex)?.id
                
                if (songId != null) {
                    val presence = songwritersnotebook.app.data.UserPresence(
                        userId = userId,
                        email = email,
                        deviceId = sessionDeviceId,
                        username = username,
                        color = userColor,
                        cursorPosition = newTextFieldValue.selection.start,
                        activeSectionId = sectionId,
                        activeLineId = lineId,
                        lastActive = Clock.System.now().toEpochMilliseconds().toDouble()
                    )
                    // DEBOUNCE: Use the debounced updater instead of immediate.
                    // This prevents cursor "jumbling" caused by constant network round-trips while typing.
                    updatePresenceWithDebounce(presence)
                }
            }
        }

        if (text.contains('\n')) {
            val parts = text.split('\n', limit = 2)
            val firstPart = parts[0]
            val secondPart = parts.getOrElse(1) { "" }

            val song = _uiState.value.song ?: return
            val section = song.sections.getOrNull(sectionIndex) ?: return
            val line = section.lines.getOrNull(lineIndex) ?: return

            // 1. Calculate structural change
            val newLines = section.lines.toMutableList()
            val updatedLine = line.copy(text = firstPart, lastModified = Clock.System.now().toEpochMilliseconds())
            newLines[lineIndex] = updatedLine

            val prevPos = updatedLine.position
            val nextPos = newLines.getOrNull(lineIndex + 1)?.position
            val newPos = FractionalIndexer.generateBetween(prevPos, nextPos)

            val newLine = Line(text = secondPart, sectionId = section.id, position = newPos)
            newLines.add(lineIndex + 1, newLine)

            val newSections = song.sections.toMutableList()
            newSections[sectionIndex] = section.copy(lines = newLines)
            val updatedSong = song.copy(sections = newSections, lastModified = Clock.System.now())

            // 2. ATOMIC UI UPDATE: Update song and focus in one single emission
            _uiState.update { it.copy(
                song = updatedSong,
                nextFocusRequest = FocusRequest(targetId = newLine.id, isSectionTitle = false, cursorPosition = TextRange.Zero)
            ) }

            // 3. BACKGROUND PERSISTENCE: Handle CRDT and metadata in background
            hasUnsavedChanges.value = true
            hasSessionEdits = true
            modifiedSectionIndices.add(sectionIndex)
            
            viewModelScope.launch(Dispatchers.Default) {
                songRepository?.pushCrdtUpdate(updatedSong.id, updatedSong, scope = songwritersnotebook.app.crdt.UndoScope.EDITOR, forceUndoStep = true)
            }
            triggerDebouncedSave()
            
            triggerSecondaryUpdates(updatedSong)
        } else {
            val song = _uiState.value.song ?: return
            val section = song.sections.getOrNull(sectionIndex) ?: return
            val line = section.lines.getOrNull(lineIndex) ?: return
            val oldText = line.text
            val newText = newTextFieldValue.text

            if (oldText == newText) return

            // PRO COLLABORATION: Atomic Diffing
            var prefixLen = 0
            while (prefixLen < oldText.length && prefixLen < newText.length && oldText[prefixLen] == newText[prefixLen]) {
                prefixLen++
            }

            var suffixLen = 0
            while (suffixLen < oldText.length - prefixLen && 
                   suffixLen < newText.length - prefixLen && 
                   oldText[oldText.length - 1 - suffixLen] == newText[newText.length - 1 - suffixLen]) {
                suffixLen++
            }

            val deleteCount = oldText.length - prefixLen - suffixLen
            val insertText = newText.substring(prefixLen, newText.length - suffixLen)

            // 1. Update Local UI immediately AND synchronously (Main Thread)
            val updatedLine = line.copy(text = newText, lastModified = Clock.System.now().toEpochMilliseconds())
            val newLines = section.lines.toMutableList()
            newLines[lineIndex] = updatedLine
            val newSections = song.sections.toMutableList()
            newSections[sectionIndex] = section.copy(lines = newLines)

            localEditsHistory.add(newText)
            if (localEditsHistory.size > 200) localEditsHistory.removeAt(0)

            _uiState.update { it.copy(
                song = song.copy(sections = newSections, lastModified = Clock.System.now()),
                selectedLineChords = parseChordsFromLine(newText),
                activeChord = null,
                validInsertionPoints = emptyList()
            ) }
            hasUnsavedChanges.value = true
            hasSessionEdits = true

            // 2. Queue Atomic Delta to CRDT in background (FIFO Channel)
            textUpdateChannel.trySend(
                TextDeltaUpdate(
                    songId = song.id,
                    sectionId = section.id,
                    lineId = line.id,
                    index = prefixLen,
                    deleteCount = deleteCount,
                    insertText = insertText,
                    isShared = isCollaborationEnabled
                )
            )

            // 3. Update Presence indicator for collaborators
            if (isCollaborationEnabled || _uiState.value.presence.isNotEmpty()) {
                val userId = currentUserId ?: "anonymous"
                val authUser = songwritersnotebook.app.utils.AuthHandler().getCurrentUser()
                val username = authUser?.displayName ?: _uiState.value.userProfile?.username ?: "Songwriter"
                val email = authUser?.email ?: _uiState.value.userProfile?.email ?: ""
                
                val presence = songwritersnotebook.app.data.UserPresence(
                    userId = userId,
                    email = email,
                    deviceId = sessionDeviceId,
                    username = username,
                    color = getUserColor(userId),
                    activeSectionId = section.id,
                    activeLineId = line.id,
                    cursorPosition = newTextFieldValue.selection.start,
                    lastActive = Clock.System.now().toEpochMilliseconds().toDouble()
                )
                updatePresenceWithDebounce(presence)
            }
            triggerDebouncedSave()
            lastKeystrokeTime = Clock.System.now().toEpochMilliseconds()
            
            // Trigger lazy secondary updates for rhymes (debounced)
            triggerSecondaryUpdates(song.copy(sections = newSections))
        }
    }
    fun onSectionTitleFocusChanged(sectionIndex: Int, isFocused: Boolean) {
        val song = _uiState.value.song ?: return
        val section = song.sections.getOrNull(sectionIndex) ?: return
        val userId = currentUserId ?: "anonymous"
        val userColor = getUserColor(userId)
        val authUser = songwritersnotebook.app.utils.AuthHandler().getCurrentUser()
        val email = authUser?.email ?: _uiState.value.userProfile?.email ?: ""

        if (isFocused) {
            _uiState.update { it.copy(
                activeSectionId = section.id,
                activeLineId = null
            ) }
            
            if (isCollaborationEnabled || _uiState.value.presence.isNotEmpty()) {
                val username = authUser?.displayName ?: _uiState.value.userProfile?.username ?: "Songwriter"
                val presence = songwritersnotebook.app.data.UserPresence(
                    userId = userId,
                    email = email,
                    deviceId = sessionDeviceId,
                    username = username,
                    color = userColor,
                    activeSectionId = section.id,
                    activeLineId = null,
                    lastActive = Clock.System.now().toEpochMilliseconds().toDouble()
                )
                updatePresenceWithDebounce(presence)
            }
        } else {
            if (_uiState.value.activeSectionId == section.id && _uiState.value.activeLineId == null) {
                _uiState.update { it.copy(activeSectionId = null) }
            }

            if (isCollaborationEnabled || _uiState.value.presence.isNotEmpty()) {
                val username = authUser?.displayName ?: _uiState.value.userProfile?.username ?: "Songwriter"
                val presence = songwritersnotebook.app.data.UserPresence(
                    userId = userId,
                    email = email,
                    deviceId = sessionDeviceId,
                    username = username,
                    color = userColor,
                    activeSectionId = null,
                    activeLineId = null,
                    lastActive = Clock.System.now().toEpochMilliseconds().toDouble()
                )
                updatePresenceWithDebounce(presence)
            }
        }
    }

    fun onLineFocusChanged(sectionIndex: Int, lineIndex: Int, isFocused: Boolean) {
        val song = _uiState.value.song ?: return
        val section = song.sections.getOrNull(sectionIndex) ?: return
        val line = section.lines.getOrNull(lineIndex) ?: return
        val userId = currentUserId ?: "anonymous"
        val userColor = getUserColor(userId)
        val authUser = songwritersnotebook.app.utils.AuthHandler().getCurrentUser()
        val email = authUser?.email ?: _uiState.value.userProfile?.email ?: ""

        if (isFocused) {
            _uiState.update { it.copy(
                lastFocusedLine = Pair(sectionIndex, lineIndex),
                activeSectionId = section.id,
                activeLineId = line.id
            ) }
            
            // Soft Focus / Presence Update
            if (isCollaborationEnabled || _uiState.value.presence.isNotEmpty()) {
                val username = authUser?.displayName ?: _uiState.value.userProfile?.username ?: "Songwriter"
                val presence = songwritersnotebook.app.data.UserPresence(
                    userId = userId,
                    email = email,
                    deviceId = sessionDeviceId,
                    username = username,
                    color = userColor,
                    activeSectionId = section.id,
                    activeLineId = line.id,
                    lastActive = Clock.System.now().toEpochMilliseconds().toDouble()
                )
                updatePresenceWithDebounce(presence)
            }
        } else {
            // Focus lost
            if (_uiState.value.activeLineId == line.id) {
                _uiState.update { it.copy(activeSectionId = null, activeLineId = null) }
            }

            if (isCollaborationEnabled || _uiState.value.presence.isNotEmpty()) {
                // Clear Presence Focus
                val username = authUser?.displayName ?: _uiState.value.userProfile?.username ?: "Songwriter"
                val presence = songwritersnotebook.app.data.UserPresence(
                    userId = userId,
                    email = email,
                    deviceId = sessionDeviceId,
                    username = username,
                    color = userColor,
                    activeSectionId = null,
                    activeLineId = null,
                    lastActive = Clock.System.now().toEpochMilliseconds().toDouble()
                )
                updatePresenceWithDebounce(presence)
            }
            // Trigger debounced save on focus loss for Cloud Songs
            if (isCollaborationEnabled && hasUnsavedChanges.value) {
        
            }
        }
    }

    fun onFocusHandled() {
        _uiState.update { it.copy(nextFocusRequest = null) }
    }

    fun navigateFocusUp(sectionIndex: Int, lineIndex: Int?, horizontalOffset: Float?) {
        val song = _uiState.value.song ?: return
        val showHeaders = _uiState.value.editorSettings.showSectionHeaders

        if (lineIndex == null) {
            val targetSectionIndex = sectionIndex - 1
            if (targetSectionIndex >= 0) {
                val targetSection = song.sections[targetSectionIndex]
                if (targetSection.lines.isNotEmpty()) {
                    val targetLine = targetSection.lines.last()
                    _uiState.update { it.copy(
                        nextFocusRequest = FocusRequest(
                            targetId = targetLine.id,
                            isSectionTitle = false,
                            horizontalOffset = horizontalOffset,
                            focusPivot = FocusPivot.BOTTOM
                        )
                    ) }
                } else if (showHeaders) {
                    _uiState.update { it.copy(
                        nextFocusRequest = FocusRequest(
                            targetId = targetSection.id,
                            isSectionTitle = true,
                            horizontalOffset = horizontalOffset,
                            focusPivot = FocusPivot.BOTTOM
                        )
                    ) }
                }
            }
        } else {
            if (lineIndex > 0) {
                val targetLine = song.sections[sectionIndex].lines[lineIndex - 1]
                _uiState.update { it.copy(
                    nextFocusRequest = FocusRequest(
                        targetId = targetLine.id,
                        isSectionTitle = false,
                        horizontalOffset = horizontalOffset,
                        focusPivot = FocusPivot.BOTTOM
                    )
                ) }
            } else {
                if (showHeaders) {
                    val targetSection = song.sections[sectionIndex]
                    _uiState.update { it.copy(
                        nextFocusRequest = FocusRequest(
                            targetId = targetSection.id,
                            isSectionTitle = true,
                            horizontalOffset = horizontalOffset,
                            focusPivot = FocusPivot.BOTTOM
                        )
                    ) }
                } else {
                    val targetSectionIndex = sectionIndex - 1
                    if (targetSectionIndex >= 0) {
                        val targetSection = song.sections[targetSectionIndex]
                        if (targetSection.lines.isNotEmpty()) {
                            val targetLine = targetSection.lines.last()
                            _uiState.update { it.copy(
                                nextFocusRequest = FocusRequest(
                                    targetId = targetLine.id,
                                    isSectionTitle = false,
                                    horizontalOffset = horizontalOffset,
                                    focusPivot = FocusPivot.BOTTOM
                                )
                            ) }
                        }
                    }
                }
            }
        }
    }

    fun navigateFocusDown(sectionIndex: Int, lineIndex: Int?, horizontalOffset: Float?) {
        val song = _uiState.value.song ?: return
        val showHeaders = _uiState.value.editorSettings.showSectionHeaders

        if (lineIndex == null) {
            val targetSection = song.sections[sectionIndex]
            if (targetSection.lines.isNotEmpty()) {
                val targetLine = targetSection.lines.first()
                _uiState.update { it.copy(
                    nextFocusRequest = FocusRequest(
                        targetId = targetLine.id,
                        isSectionTitle = false,
                        horizontalOffset = horizontalOffset,
                        focusPivot = FocusPivot.TOP
                    )
                ) }
            } else {
                val targetSectionIndex = sectionIndex + 1
                if (targetSectionIndex < song.sections.size) {
                    val nextSection = song.sections[targetSectionIndex]
                    if (showHeaders) {
                        _uiState.update { it.copy(
                            nextFocusRequest = FocusRequest(
                                targetId = nextSection.id,
                                isSectionTitle = true,
                                horizontalOffset = horizontalOffset,
                                focusPivot = FocusPivot.TOP
                            )
                        ) }
                    } else if (nextSection.lines.isNotEmpty()) {
                        val targetLine = nextSection.lines.first()
                        _uiState.update { it.copy(
                            nextFocusRequest = FocusRequest(
                                targetId = targetLine.id,
                                isSectionTitle = false,
                                horizontalOffset = horizontalOffset,
                                focusPivot = FocusPivot.TOP
                            )
                        ) }
                    }
                }
            }
        } else {
            val currentSection = song.sections[sectionIndex]
            if (lineIndex < currentSection.lines.size - 1) {
                val targetLine = currentSection.lines[lineIndex + 1]
                _uiState.update { it.copy(
                    nextFocusRequest = FocusRequest(
                        targetId = targetLine.id,
                        isSectionTitle = false,
                        horizontalOffset = horizontalOffset,
                        focusPivot = FocusPivot.TOP
                    )
                ) }
            } else {
                val targetSectionIndex = sectionIndex + 1
                if (targetSectionIndex < song.sections.size) {
                    val nextSection = song.sections[targetSectionIndex]
                    if (showHeaders) {
                        _uiState.update { it.copy(
                            nextFocusRequest = FocusRequest(
                                targetId = nextSection.id,
                                isSectionTitle = true,
                                horizontalOffset = horizontalOffset,
                                focusPivot = FocusPivot.TOP
                            )
                        ) }
                    } else if (nextSection.lines.isNotEmpty()) {
                        val targetLine = nextSection.lines.first()
                        _uiState.update { it.copy(
                            nextFocusRequest = FocusRequest(
                                targetId = targetLine.id,
                                isSectionTitle = false,
                                horizontalOffset = horizontalOffset,
                                focusPivot = FocusPivot.TOP
                            )
                        ) }
                    }
                }
            }
        }
    }
    
    // --- Selection & Editing ---
    fun insertChordAtCursor(sectionIndex: Int, lineIndex: Int, cursorPosition: Int, chord: String) {
        if (isViewer) return
        val currentSong = _uiState.value.song ?: return
        val line = currentSong.sections[sectionIndex].lines[lineIndex]
        val lineText = line.text
        val existingChords = parseChordsFromLine(lineText)
        val containingChord = existingChords.find { cursorPosition > it.range.first && cursorPosition <= it.range.last }

        val chordTag = "[$chord]"
        val (insertionPosition, newCursorPosition) = if (containingChord != null) {
            val midPoint = (containingChord.range.first + containingChord.range.last) / 2
            val newPos = if (cursorPosition <= midPoint) containingChord.range.first else containingChord.range.last + 1
            Pair(newPos, newPos + chordTag.length)
        } else {
            Pair(cursorPosition, cursorPosition + chordTag.length)
        }

        val newText = lineText.substring(0, insertionPosition) + chordTag + lineText.substring(insertionPosition)
        updateLineText(sectionIndex, lineIndex, TextFieldValue(newText, selection = TextRange(newCursorPosition)))
        
        val state = _uiState.value
        if (state.activeProgressionId != null) {
            val prog = currentSong.progressions.find { p -> p.id == state.activeProgressionId }
            if (prog != null && prog.chordNames.isNotEmpty()) {
                val nextIndex = (state.activeProgressionIndex + 1) % prog.chordNames.size
                _uiState.update { 
                    it.copy(
                        selectedChordToPlace = prog.chordNames[nextIndex],
                        activeProgressionIndex = nextIndex
                    )
                }
            } else {
                _uiState.update { it.copy(selectedChordToPlace = null, activeProgressionId = null, activeProgressionIndex = 0) }
            }
        } else {
            _uiState.update { it.copy(selectedChordToPlace = null) }
        }
    }
    
    fun selectItem(sectionIndex: Int, lineIndex: Int?) {
        val currentSong = _uiState.value.song ?: return
        val currentSelection = _uiState.value.selectedItem

        val targetId: String
        val newLineChords: List<ChordInLine>

        if (lineIndex != null) {
            val line =
                currentSong.sections.getOrNull(sectionIndex)?.lines?.getOrNull(lineIndex) ?: return
            targetId = line.id
            newLineChords = parseChordsFromLine(line.text)
        } else {
            targetId = currentSong.sections.getOrNull(sectionIndex)?.id ?: return
            newLineChords = emptyList()
        }

        if (currentSelection?.id == targetId) {
            cancelSelection()
        } else {
            _uiState.update {
                it.copy(
                    selectedItem = ItemSelection(id = targetId, sectionIndex = sectionIndex, lineIndex = lineIndex),
                    selectedLineChords = newLineChords,
                    activeChord = null
                )
            }
        }
    }

    fun cancelSelection() {
        _uiState.update {
            it.copy(selectedItem = null, selectedLineChords = emptyList(), activeChord = null, validInsertionPoints = emptyList())
        }
    }

    fun deleteSelectedItem() {
        if (isViewer) return
        val selection = _uiState.value.selectedItem ?: return
        val currentSong = _uiState.value.song ?: return
        
        var deletedSecId: String? = null
        var deletedLinId: String? = null
        
        val updatedSong = if (selection.lineIndex != null) {
            deletedLinId = selection.id
            SongEditorUtils.deleteLine(currentSong, selection.sectionIndex, selection.lineIndex)
        } else {
            deletedSecId = selection.id
            SongEditorUtils.deleteSection(currentSong, selection.sectionIndex)
        }
        
        updateSong(
            updatedSong, 
            sectionIndex = if (selection.lineIndex != null) selection.sectionIndex else null,
            deletedSectionId = deletedSecId,
            deletedLineId = deletedLinId
        )
        cancelSelection()
    }

    fun moveSelectedItemUp() {
        if (isViewer) return
        val selection = _uiState.value.selectedItem ?: return
        val currentSong = _uiState.value.song ?: return
        val updatedSong = if (selection.lineIndex != null) {
            SongEditorUtils.moveLineUp(currentSong, selection.sectionIndex, selection.lineIndex)
        } else {
            SongEditorUtils.moveSectionUp(currentSong, selection.sectionIndex)
        }
        updateSong(updatedSong, sectionIndex = if (selection.lineIndex != null) selection.sectionIndex else null, forceUndoStep = true)
        findAndReselectItem(selection.id)
    }

    fun moveSelectedItemDown() {
        if (isViewer) return
        val selection = _uiState.value.selectedItem ?: return
        val currentSong = _uiState.value.song ?: return
        val updatedSong = if (selection.lineIndex != null) {
            SongEditorUtils.moveLineDown(currentSong, selection.sectionIndex, selection.lineIndex)
        } else {
            SongEditorUtils.moveSectionDown(currentSong, selection.sectionIndex)
        }
        updateSong(updatedSong, sectionIndex = if (selection.lineIndex != null) selection.sectionIndex else null, forceUndoStep = true)
        findAndReselectItem(selection.id)
    }

    fun duplicateSelectedItem() {
        if (isViewer) return
        val selection = _uiState.value.selectedItem ?: return
        val currentSong = _uiState.value.song ?: return
        val updatedSong = if (selection.lineIndex != null) {
            SongEditorUtils.duplicateLine(currentSong, selection.sectionIndex, selection.lineIndex)
        } else {
            SongEditorUtils.duplicateSection(currentSong, selection.sectionIndex)
        }
        updateSong(updatedSong, sectionIndex = if (selection.lineIndex != null) selection.sectionIndex else null, forceUndoStep = true)
        cancelSelection()
    }
    
    fun addSection() {
        if (isViewer) return
        val currentSong = _uiState.value.song ?: return

        // Intelligent Placement: 
        // If we have a focus, add relative to it.
        // If NO focus (cursor not placed), use the clean appendSection logic.
        val focusLine = _uiState.value.lastFocusedLine
        val (updatedSong, newSectionId) = if (focusLine != null) {
            val sectionIdx = focusLine.first.coerceIn(0, currentSong.sections.size - 1)
            val lineIdx = focusLine.second.coerceIn(0, (currentSong.sections[sectionIdx].lines.size - 1).coerceAtLeast(0))
            SongEditorUtils.addSection(currentSong, sectionIdx, lineIdx)
        } else {
            // Append to the absolute bottom without splitting anything
            SongEditorUtils.appendSection(currentSong)
        }

        _uiState.update { it.copy(nextFocusRequest = FocusRequest(targetId = newSectionId, isSectionTitle = true, cursorPosition = TextRange("New Section".length))) }
        updateSong(updatedSong, forceUndoStep = true)
    }

    fun handleSectionTitleEnter(sectionIndex: Int) {
        if (isViewer) return
        val currentSong = _uiState.value.song ?: return
        val section = currentSong.sections.getOrNull(sectionIndex) ?: return

        // UX: If section is empty, pressing Enter on title creates the first line
        if (section.lines.isEmpty()) {
            val newLine = Line(text = "", sectionId = section.id)
            val updatedSections = currentSong.sections.toMutableList()
            updatedSections[sectionIndex] = section.copy(lines = listOf(newLine))
            
            _uiState.update { it.copy(nextFocusRequest = FocusRequest(targetId = newLine.id, isSectionTitle = false, cursorPosition = TextRange.Zero)) }
            updateSong(currentSong.copy(sections = updatedSections), forceUndoStep = true)
        } else {
            // If not empty, move focus to the first existing line
            val firstLineId = section.lines.first().id
            _uiState.update { it.copy(nextFocusRequest = FocusRequest(targetId = firstLineId, isSectionTitle = false, cursorPosition = TextRange.Zero)) }
        }
    }
    fun deleteSectionHeaderOrMerge(sectionIndex: Int) {
        if (isViewer || sectionIndex == 0) return
        mergeSectionWithPrevious(sectionIndex)
    }

    fun splitLine(sectionIndex: Int, lineIndex: Int, textFieldValueWithNewline: TextFieldValue) {
        if (isViewer) return
        val currentSong = _uiState.value.song ?: return
        val text = textFieldValueWithNewline.text
        val selectionStart = textFieldValueWithNewline.selection.start
        val splitPoint = text.lastIndexOf('\n', startIndex = (selectionStart - 1).coerceAtLeast(0)).takeIf { it != -1 } ?: return
        val textForCurrentLine = text.substring(0, splitPoint)
        val textForNewLine = text.substring(splitPoint + 1)
        val (updatedSong, newLineId) = SongEditorUtils.splitLine(currentSong, sectionIndex, lineIndex, textForCurrentLine, textForNewLine)
        _uiState.update {
            it.copy(nextFocusRequest = FocusRequest(targetId = newLineId, cursorPosition = TextRange.Zero))
        }
        updateSong(updatedSong, forceUndoStep = true)
    }

    fun mergeSectionWithPrevious(sectionIndex: Int) {
        if (isViewer) return
        val currentSong = _uiState.value.song ?: return
        if (sectionIndex == 0) return

        val previousSection = currentSong.sections[sectionIndex - 1]
        val focusTargetId: String
        val focusCursorPosition: Int

        if (previousSection.lines.isNotEmpty()) {
            focusTargetId = previousSection.lines.last().id
            focusCursorPosition = previousSection.lines.last().text.length
        } else {
            focusTargetId = previousSection.id
            focusCursorPosition = previousSection.title.length
        }

        val updatedSong = SongEditorUtils.mergeSectionWithPrevious(currentSong, sectionIndex)

        _uiState.update {
            it.copy(
                nextFocusRequest = FocusRequest(
                    targetId = focusTargetId,
                    isSectionTitle = false,
                    cursorPosition = TextRange(focusCursorPosition)
                )
            )
        }
        updateSong(updatedSong, forceUndoStep = true)
    }

    fun mergeLineWithPrevious(sectionIndex: Int, lineIndex: Int, currentText: String) {
        if (isViewer) return
        val currentSong = _uiState.value.song ?: return
        if (sectionIndex == 0 && lineIndex == 0) return

        val currentLine = currentSong.sections.getOrNull(sectionIndex)?.lines?.getOrNull(lineIndex) ?: return
        val lineId = currentLine.id
        
        // PRO STRATEGIC FRICTION: If the line has "baggage" (tabs or recordings), 
        // we pause and ask the user what to do with the media.
        val hasTabs = currentLine.tabs.isNotEmpty()
        val hasRecordings = currentSong.recordings[lineId]?.isNotEmpty() == true
        
        if (hasTabs || hasRecordings) {
            _uiState.update { 
                it.copy(
                    itemToDelete = ItemSelection(
                        id = lineId, 
                        sectionIndex = sectionIndex, 
                        lineIndex = lineIndex,
                        isMerge = true,
                        mergeText = currentText
                    )
                )
            }
            return
        }

        var focusTargetId: String = ""
        var focusCursorPosition: Int = 0
        var shouldMerge = false

        if (lineIndex > 0) {
            val previousLine = currentSong.sections[sectionIndex].lines[lineIndex - 1]
            focusTargetId = previousLine.id
            focusCursorPosition = previousLine.text.length
            shouldMerge = true
        } else if (sectionIndex > 0) {
            val prevSectionLines = currentSong.sections[sectionIndex - 1].lines
            if (prevSectionLines.isNotEmpty()) {
                focusTargetId = prevSectionLines.last().id
                focusCursorPosition = prevSectionLines.last().text.length
                shouldMerge = true
            }
        }

        if (shouldMerge) {
            val updatedSong = SongEditorUtils.mergeLineWithPrevious(
                currentSong,
                sectionIndex,
                lineIndex,
                currentText
            )

            _uiState.update {
                it.copy(
                    nextFocusRequest = FocusRequest(
                        targetId = focusTargetId,
                        cursorPosition = TextRange(focusCursorPosition)
                    )
                )
            }
            updateSong(
                updatedSong, 
                sectionIndex = sectionIndex, 
                deletedLineId = lineId, 
                forceUndoStep = true
            )
        }
    }
    
    // --- Chord Panel & Manager ---
    fun playChordByName(chordName: String, overrideInstrument: String? = null) {
        val state = _uiState.value
        val song = state.song ?: return
        val instrument = overrideInstrument ?: state.selectedInstrument
        
        val chordDef = song.chordDefinitions[chordName]
        
        val notesToPlay = if (instrument == "guitar") {
            val tab = chordDef?.guitarTab?.ifEmpty { null }?.map { it.toIntOrNull() ?: if (it == "x") 'x' else 0 } 
                ?: songwritersnotebook.app.utils.ChordUtils.getTabForCapo(songwritersnotebook.app.utils.ChordUtils.parseChordName(chordName).first, songwritersnotebook.app.utils.ChordUtils.parseChordName(chordName).second, song.guitarCapo)
                ?: songwritersnotebook.app.utils.ChordUtils.GUITAR_CHORD_TABS[chordName]
            
            tab?.let { 
                songwritersnotebook.app.utils.ChordUtils.tabToNotes(it, song.guitarTuning, song.guitarCapo) 
            } ?: emptyList()
        } else {
            chordDef?.pianoNotes?.ifEmpty { null } ?: run {
                val (root, quality) = songwritersnotebook.app.utils.ChordUtils.parseChordName(chordName)
                songwritersnotebook.app.utils.ChordUtils.PIANO_CHORD_VOICINGS[chordName]
                    ?: songwritersnotebook.app.utils.ChordUtils.calculateChordNotes(root, quality)
            }
        }

        if (notesToPlay.isNotEmpty()) {
            val safeOffset = if (state.strumOffset.isNaN()) 40f else state.strumOffset
            val strumDelayMs = (safeOffset / 120f * 100).toLong().coerceAtLeast(10)
            
            // Play first note synchronously to satisfy Web Audio API's user gesture requirements
            audioHandler.playNote(notesToPlay.first(), instrument, 1.0f)
            
            if (notesToPlay.size > 1) {
                viewModelScope.launch {
                    if (strumDelayMs > 0) delay(strumDelayMs)
                    notesToPlay.drop(1).forEach { note ->
                        audioHandler.playNote(note, instrument, 1.0f)
                        if (strumDelayMs > 0) {
                            delay(strumDelayMs)
                        }
                    }
                }
            }
        }
    }

    fun selectChordForPlacement(chord: String) {
        _uiState.update { 
            if (it.activeProgressionId != null) {
                it.copy(selectedChordToPlace = chord, activeProgressionId = null, activeProgressionIndex = 0)
            } else {
                it.copy(selectedChordToPlace = if (it.selectedChordToPlace == chord) null else chord)
            }
        }
    }

    fun setActiveProgression(id: String?) {
        val song = _uiState.value.song ?: return
        if (id == null) {
            _uiState.update { it.copy(activeProgressionId = null, activeProgressionIndex = 0, selectedChordToPlace = null) }
        } else {
            val prog = song.progressions.find { it.id == id }
            if (prog != null && prog.chordNames.isNotEmpty()) {
                _uiState.update { 
                    it.copy(
                        activeProgressionId = id, 
                        activeProgressionIndex = 0, 
                        selectedChordToPlace = prog.chordNames[0]
                    ) 
                }
            }
        }
    }

    fun onAddProgressionClicked() {
        _uiState.update { it.copy(showProgressionBuilderDialog = true) }
    }

    fun onProgressionBuilderDismissed() {
        _uiState.update { it.copy(showProgressionBuilderDialog = false) }
    }

    fun saveProgression(progression: songwritersnotebook.app.data.ChordProgression) {
        val song = _uiState.value.song ?: return
        
        // Make sure all chords used in the progression exist in the library. If not, we might need to add generic ones.
        // For now, assume they exist or are defaults.
        
        val updatedProgressions = song.progressions.filterNot { it.id == progression.id } + progression
        val updatedSong = song.copy(progressions = updatedProgressions)
        
        updateSong(updatedSong, forceUndoStep = true)
        
        // Sync favorite status with the repository
        viewModelScope.launch {
            val existing = _uiState.value.favoriteProgressions.find { it.progression.id == progression.id }
            if (progression.isFavorite && existing == null) {
                chordLibraryRepository?.toggleFavoriteProgression(progression)
            } else if (!progression.isFavorite && existing != null) {
                chordLibraryRepository?.toggleFavoriteProgression(progression)
            } else if (progression.isFavorite && existing != null) {
                chordLibraryRepository?.updateFavoriteProgression(progression)
            }
        }
        
        _uiState.update { it.copy(showProgressionBuilderDialog = false) }
    }

    fun updateProgression(progressionId: String, name: String?, chordNames: List<String>?, keyRoot: String?, isMajor: Boolean?) {
        val song = _uiState.value.song ?: return
        val existingProgression = song.progressions.find { it.id == progressionId } ?: return
        
        val updatedProg = existingProgression.copy(
            name = name ?: existingProgression.name,
            chordNames = chordNames ?: existingProgression.chordNames,
            keyRoot = keyRoot ?: existingProgression.keyRoot,
            isMajor = isMajor ?: existingProgression.isMajor,
            lastModified = kotlinx.datetime.Clock.System.now().toEpochMilliseconds()
        )
        saveProgression(updatedProg)
    }

    fun deleteProgression(progressionId: String) {
        val song = _uiState.value.song ?: return
        val updatedProgressions = song.progressions.filterNot { it.id == progressionId }
        val updatedSong = song.copy(progressions = updatedProgressions)
        updateSong(updatedSong, forceUndoStep = true)
        
        val activeProgId = _uiState.value.activeProgressionId
        if (activeProgId == progressionId) {
            _uiState.update { it.copy(activeProgressionId = null, activeProgressionIndex = 0, selectedChordToPlace = null) }
        }
    }

    fun onAddChordClicked() {
        initChordCreator(null)
    }

    fun onEditChordClicked() {
        val selected = _uiState.value.selectedChordToPlace ?: return
        initChordCreator(selected)
    }

    fun onDeleteChordClicked(chord: String) {
        _uiState.update { it.copy(chordToDelete = chord) }
    }

    fun setInstrument(instrument: String) {
        _uiState.update { it.copy(selectedInstrument = instrument) }
    }

    fun onStrumOffsetChange(offset: Float) {
        _uiState.update { it.copy(strumOffset = offset) }
    }
    
    fun selectActiveChord(chordToActivate: ChordInLine) {
        val song = _uiState.value.song ?: return
        val selection = _uiState.value.selectedItem ?: return
        val line = song.sections.getOrNull(selection.sectionIndex)?.lines?.getOrNull(selection.lineIndex ?: -1) ?: return

        _uiState.update {
            val newActiveChord = if (it.activeChord?.index == chordToActivate.index) null else chordToActivate
            val points = if (newActiveChord != null) getValidInsertionPoints(line.text.removeRange(newActiveChord.range)) else emptyList()
            it.copy(activeChord = newActiveChord, validInsertionPoints = points)
        }
    }

    fun updateActiveChordPosition(insertionPoint: Int) {
        if (isViewer) return
        val active = _uiState.value.activeChord ?: return
        val selection = _uiState.value.selectedItem ?: return
        val lineIndex = selection.lineIndex ?: return
        val sectionIndex = selection.sectionIndex
        val song = _uiState.value.song ?: return

        val line = song.sections[sectionIndex].lines[lineIndex]
        
        // ROBUSTNESS FIX: Always re-parse the chord's actual position in the current line text
        // to prevent any race conditions or state desyncs during rapid sliding.
        val currentChords = parseChordsFromLine(line.text)
        val currentActive = currentChords.find { it.name == active.name && it.index == active.index } 
            ?: currentChords.find { it.name == active.name } 
            ?: return

        // Safety check: ensure insertionPoint is within bounds of textWithoutChord
        val textWithoutChord = line.text.removeRange(currentActive.range)
        val safeInsertionPoint = insertionPoint.coerceIn(0, textWithoutChord.length)
        
        val chordTag = "[" + currentActive.name + "]"
        val newText = textWithoutChord.substring(0, safeInsertionPoint) + chordTag + textWithoutChord.substring(safeInsertionPoint)

        val updatedSong = song.copy(sections = song.sections.mapIndexed { sIdx, section ->
            if (sIdx == sectionIndex) {
                section.copy(lines = section.lines.mapIndexed { lIdx, l -> if (lIdx == lineIndex) l.copy(text = newText) else l })
            } else section
        })

        val newChords = parseChordsFromLine(newText)
        val newActiveChord = newChords.find { it.range.first == insertionPoint }
        val newValidPoints = if (newActiveChord != null) getValidInsertionPoints(newText.removeRange(newActiveChord.range)) else emptyList()

        localEditsHistory.add(newText)
        if (localEditsHistory.size > 200) localEditsHistory.removeAt(0)

        _uiState.update { it.copy(selectedLineChords = newChords, activeChord = newActiveChord, validInsertionPoints = newValidPoints) }
        updateSong(updatedSong, sectionIndex = sectionIndex, forceUndoStep = false, pushToDatabase = false)
    }

    fun flushActiveChordPosition() {
        if (isViewer) return
        val currentSong = _uiState.value.song ?: return
        viewModelScope.launch(Dispatchers.Default) {
            songRepository?.pushCrdtUpdate(currentSong.id, currentSong, scope = songwritersnotebook.app.crdt.UndoScope.EDITOR, forceUndoStep = true)
        }
    }

    fun deleteActiveChord() {
        if (isViewer) return
        val active = _uiState.value.activeChord ?: return
        val selection = _uiState.value.selectedItem ?: return
        val lineIndex = selection.lineIndex ?: return
        val sectionIndex = selection.sectionIndex
        val song = _uiState.value.song ?: return

        val line = song.sections[sectionIndex].lines[lineIndex]
        val newText = line.text.removeRange(active.range)

        val updatedSong = song.copy(sections = song.sections.mapIndexed { sIdx, section ->
            if (sIdx == sectionIndex) {
                section.copy(lines = section.lines.mapIndexed { lIdx, l -> if (lIdx == lineIndex) l.copy(text = newText) else l })
            } else section
        })

        localEditsHistory.add(newText)
        if (localEditsHistory.size > 200) localEditsHistory.removeAt(0)

        _uiState.update { it.copy(selectedLineChords = parseChordsFromLine(newText), activeChord = null, validInsertionPoints = emptyList()) }
        updateSong(updatedSong, sectionIndex = sectionIndex, forceUndoStep = true)
    }
    
    // --- Chord Creator ---
    fun initChordCreator(chordName: String?) {
        // Chord Creator allowed for Viewers? Prompt says "add, edit, or delete chords".
        // Viewers can view chord diagrams.
        // But `saveNewChord` should be blocked.
        // So `initChordCreator` is fine (read-only view of chord info).
        
        val (root, qualityKey) = songwritersnotebook.app.utils.ChordUtils.parseChordName(chordName ?: "")
        // ... (rest of function)
        _uiState.update { currentState ->
            val currentInstrument = currentState.selectedInstrument
            val currentTuning = currentState.guitarTuning
            val currentCapo = currentState.guitarCapo
            val initialFretStart = (currentCapo - 1).coerceAtLeast(0)

            val chordDef = if (chordName != null) {
                currentState.song?.chordDefinitions?.get(chordName)
            } else {
                null
            }

            val initialTab: List<Any>
            val initialPianoNotes: List<String>
            val initialIsCustom: Boolean
            val initialRoot: String
            val initialQuality: String

            if (chordDef != null && chordDef.isCustom) {
                initialPianoNotes = chordDef.pianoNotes ?: emptyList()
                initialIsCustom = true
                initialRoot = ""
                initialQuality = ""
                initialTab = chordDef.guitarTab?.map { fretString ->
                    fretString.toIntOrNull() ?: if (fretString == "x") 'x' else 0
                } ?: listOf('x', 'x', 'x', 'x', 'x', 'x')
            } else if (chordName != null) {
                initialPianoNotes = songwritersnotebook.app.utils.ChordUtils.PIANO_CHORD_VOICINGS[chordName]
                    ?: songwritersnotebook.app.utils.ChordUtils.calculateChordNotes(root, qualityKey)
                val relativeTabShape = songwritersnotebook.app.utils.ChordUtils.getTabForCapo(root, qualityKey, currentCapo)
                    ?: listOf('x', 'x', 'x', 'x', 'x', 'x')
                initialTab = relativeTabShape.map { fret ->
                    when (fret) {
                        is Int -> if (fret > 0) fret + currentCapo else currentCapo
                        else -> fret
                    }
                }
                initialIsCustom = false
                initialRoot = root 
                initialQuality = qualityKey
            } else {
                val (defaultRoot, defaultQualityKey) = "C" to "maj"
                val targetChordName = songwritersnotebook.app.utils.ChordUtils.getChordName(defaultRoot, defaultQualityKey)
                initialPianoNotes = songwritersnotebook.app.utils.ChordUtils.PIANO_CHORD_VOICINGS[targetChordName]
                    ?: songwritersnotebook.app.utils.ChordUtils.calculateChordNotes(defaultRoot, defaultQualityKey)
                val relativeTabShape = 
                    songwritersnotebook.app.utils.ChordUtils.getTabForCapo(defaultRoot, defaultQualityKey, currentCapo)
                        ?: listOf('x', '3', '2', '0', '1', '0')
                initialTab = relativeTabShape.map { fret ->
                    when (fret) {
                        is Int -> if (fret > 0) fret + currentCapo else currentCapo
                        else -> fret
                    }
                }
                initialIsCustom = false
                initialRoot = defaultRoot
                initialQuality = defaultQualityKey
            }

            currentState.copy(
                selectedInstrument = currentInstrument,
                guitarTuning = currentTuning,
                guitarCapo = currentCapo,
                selectedChordRoot = initialRoot,
                selectedChordQuality = initialQuality,
                currentTab = initialTab,
                manualKeys = initialPianoNotes,
                isCustom = initialIsCustom,
                chordToEdit = chordName,
                showChordCreatorDialog = true,
                keyboardOctave = if (currentInstrument == "bass") 2 else 4,
                fretWindowStart = initialFretStart
            )
        }
        checkIfChordIsFavorited()
    }

    fun getChordCreatorUiState(): ChordCreatorUiState {
        return ChordCreatorUiState(
            keyboardOctave = _uiState.value.keyboardOctave,
            manualKeys = _uiState.value.manualKeys,
            selectedInstrument = _uiState.value.selectedInstrument,
            selectedChordRoot = _uiState.value.selectedChordRoot,
            selectedChordQuality = _uiState.value.selectedChordQuality,
            selectedQualityCategoryIndex = _uiState.value.selectedQualityCategoryIndex,
            guitarTuning = _uiState.value.guitarTuning,
            guitarCapo = _uiState.value.guitarCapo,
            currentTab = _uiState.value.currentTab,
            isCustom = _uiState.value.isCustom
        )
    }

    fun onChordCreatorDismissed() {
        _uiState.update { it.copy(showChordCreatorDialog = false, chordToEdit = null, manualKeys = emptyList()) }
    }

    fun saveNewChord(chordName: String) {
        if (isViewer) {
            _uiState.update { it.copy(showChordCreatorDialog = false) }
            return
        }
        if (chordName.isBlank()) {
            _uiState.update { it.copy(showChordCreatorDialog = false) }
            return
        }

        val trimmedName = chordName.trim()
        val currentChordToEdit = _uiState.value.chordToEdit
        val currentSong = _uiState.value.song ?: return
        
        val state = _uiState.value
        val isCustom = state.isCustom
        val finalPianoNotes: List<String>?
        val finalGuitarTab: List<String>?

        if (isCustom) {
            finalPianoNotes = state.manualKeys.takeIf { it.isNotEmpty() }
            val tabIsMeaningful = state.currentTab.any { (it is Int && it != 0) || (it is Char && it != 'x') }
            if (tabIsMeaningful) {
                finalGuitarTab = state.currentTab.map { it.toString() }
            } else {
                val tabIsDefined = state.currentTab.any { it is Int }
                finalGuitarTab = if (tabIsDefined) {
                    state.currentTab.map { it.toString() }
                } else {
                    null
                }
            }
        } else {
            val presetName = songwritersnotebook.app.utils.ChordUtils.getChordName(
                state.selectedChordRoot,
                state.selectedChordQuality
            )
            finalPianoNotes = songwritersnotebook.app.utils.ChordUtils.PIANO_CHORD_VOICINGS[presetName]
                ?: songwritersnotebook.app.utils.ChordUtils.calculateChordNotes(
                    state.selectedChordRoot,
                    state.selectedChordQuality
                )
            finalGuitarTab = songwritersnotebook.app.utils.ChordUtils.GUITAR_CHORD_TABS[presetName]?.map { it.toString() }
        }

        val (rootToSave, qualityToSave) = if (isCustom) {
            Pair(null, null)
        } else {
            Pair(state.selectedChordRoot, state.selectedChordQuality)
        }

        val newDefinition = songwritersnotebook.app.data.ChordDefinition(
            name = trimmedName,
            root = rootToSave,
            quality = qualityToSave,
            pianoNotes = finalPianoNotes,
            isCustom = isCustom,
            guitarTab = finalGuitarTab
        )
        
        val updatedDefinitions = currentSong.chordDefinitions.toMutableMap()
        
        if (currentChordToEdit != null && currentChordToEdit != trimmedName) {
            updatedDefinitions.remove(currentChordToEdit)
            updatedDefinitions[trimmedName] = newDefinition
            
            val regex = """\[${Regex.escape(currentChordToEdit)}\]""".toRegex()
            val updatedSections = currentSong.sections.map { section ->
                section.copy(lines = section.lines.map { line ->
                    line.copy(text = line.text.replace(regex, "[" + trimmedName + "]"))
                })
            }
            updateSong(currentSong.copy(chordDefinitions = updatedDefinitions, sections = updatedSections), chordLibraryChanged = true)
        } else {
             updatedDefinitions[trimmedName] = newDefinition
             updateSong(currentSong.copy(chordDefinitions = updatedDefinitions), chordLibraryChanged = true)
        }

        _uiState.update { currentState ->
            currentState.copy(
                selectedChordToPlace = if (currentState.selectedChordToPlace == currentChordToEdit) trimmedName else currentState.selectedChordToPlace,
                showChordCreatorDialog = false,
                chordToEdit = null,
                manualKeys = emptyList()
            )
        }
    }

    fun playCurrentChordInCreator() {
         val state = _uiState.value
         val notesToPlay: List<String>
         if (state.selectedInstrument == "guitar") {
             notesToPlay = songwritersnotebook.app.utils.ChordUtils.tabToNotes(state.currentTab, state.guitarTuning, state.guitarCapo)
         } else {
             notesToPlay = state.manualKeys
         }
         
         if (notesToPlay.isNotEmpty()) {
            val safeOffset = if (state.strumOffset.isNaN()) 40f else state.strumOffset
            val strumDelayMs = (safeOffset / 120f * 100).toLong().coerceAtLeast(10)
            viewModelScope.launch {
                notesToPlay.forEachIndexed { index, note ->
                    audioHandler.playNote(note, state.selectedInstrument)
                    if (index < notesToPlay.size - 1 && strumDelayMs > 0)kotlinx.coroutines.delay(strumDelayMs)
                }
            }
        }
    }

    fun shuffleChordSelection() {
        val randomRoot = songwritersnotebook.app.utils.ChordUtils.ROOT_NOTES.random()
        val randomQualityKey = songwritersnotebook.app.utils.ChordUtils.CHORD_QUALITIES.keys.random()
        val index = songwritersnotebook.app.utils.ChordUtils.CHORD_QUALITY_CATEGORIES.indexOfFirst {
            it.qualities.contains(randomQualityKey)
        }
        val newCategoryIndex = if (index == -1) 0 else index
        
        onPresetChordSelected(randomRoot, randomQualityKey)
        _uiState.update { it.copy(selectedQualityCategoryIndex = newCategoryIndex) }
        playChordPreset(randomRoot, randomQualityKey)
    }

    fun onPresetChordSelected(root: String, qualityKey: String) {
        _uiState.update { currentState ->
            val capo = currentState.guitarCapo
            val activeInstrument = currentState.selectedInstrument
            val currentIsCustom = currentState.isCustom

            val targetChordName = songwritersnotebook.app.utils.ChordUtils.getChordName(root, qualityKey)

            val presetPianoNotes = songwritersnotebook.app.utils.ChordUtils.PIANO_CHORD_VOICINGS[targetChordName]
                ?: songwritersnotebook.app.utils.ChordUtils.calculateChordNotes(root, qualityKey)
            val presetRelativeTab = songwritersnotebook.app.utils.ChordUtils.getTabForCapo(root, qualityKey, capo)
                ?: listOf('x', 'x', 'x', 'x', 'x', 'x')
            val presetAbsoluteTab = presetRelativeTab.map { fret ->
                when (fret) {
                    is Int -> if (fret > 0) fret + capo else capo
                    else -> fret
                }
            }

            val lowestFret = presetRelativeTab.filterIsInstance<Int>().filter { it > 0 }.minOrNull() ?: 0
            val newFretWindowStart = (lowestFret - 1).coerceAtLeast(0)

            val finalPianoNotes: List<String>
            val finalGuitarTab: List<Any>
            val newIsCustom: Boolean

            if (!currentIsCustom) {
                finalPianoNotes = presetPianoNotes
                finalGuitarTab = presetAbsoluteTab
                newIsCustom = false
            } else {
                val currentPianoNotes = currentState.manualKeys
                val currentGuitarTab = currentState.currentTab

                finalPianoNotes = if (activeInstrument.equals("piano", ignoreCase = true)) {
                    presetPianoNotes
                } else {
                    currentPianoNotes
                }

                finalGuitarTab = if (activeInstrument.equals("guitar", ignoreCase = true)) {
                    presetAbsoluteTab
                } else {
                    currentGuitarTab
                }

                val pianoMatches = (finalPianoNotes == presetPianoNotes)
                val guitarMatches = (finalGuitarTab == presetAbsoluteTab)
                newIsCustom = !(pianoMatches && guitarMatches)
            }

            currentState.copy(
                isCustom = newIsCustom,
                selectedChordRoot = root,
                selectedChordQuality = qualityKey,
                manualKeys = finalPianoNotes,
                currentTab = finalGuitarTab,
                fretWindowStart = newFretWindowStart
            )
        }
        checkIfChordIsFavorited()
        playChordPreset(root, qualityKey)
    }

    fun selectQualityCategory(index: Int) {
        _uiState.update { it.copy(selectedQualityCategoryIndex = index) }
    }

    fun playChordPreset(root: String, qualityKey: String) {
         playCurrentChordInCreator()
    }

    fun updateFretWindowStart(newStart: Int) {
        _uiState.update { it.copy(fretWindowStart = newStart.coerceIn(0, 18)) }
    }

    fun setKeyboardOctave(delta: Int) {
        _uiState.update {
            val newOctave = (it.keyboardOctave + delta).coerceIn(0, 7)
            it.copy(keyboardOctave = newOctave)
        }
    }

    fun clearManualKeys() {
        _uiState.update { it.copy(manualKeys = emptyList()) }
    }

    fun toggleManualKey(noteName: String) {
        _uiState.update { currentState ->
            val currentKeys = currentState.manualKeys
            val newKeys = if (currentKeys.contains(noteName)) {
                currentKeys - noteName
            } else {
                currentKeys + noteName
            }
            currentState.copy(
                manualKeys = newKeys,
                isCustom = true,
                selectedChordRoot = "",
                selectedChordQuality = ""
            )
        }
        checkIfChordIsFavorited()
        playCurrentChordInCreator()
    }

    fun onTabChange(newTab: List<Any>) {
        _uiState.update { currentState ->
            currentState.copy(
                currentTab = newTab,
                isCustom = true,
                selectedChordRoot = "",
                selectedChordQuality = ""
            )
        }
        checkIfChordIsFavorited()
        playCurrentChordInCreator()
    }

    fun onTuningChange(stringIndex: Int, step: Int) {
        val currentState = _uiState.value
        val currentTuning = currentState.guitarTuning.toMutableList()
        val currentNote = currentTuning.getOrNull(stringIndex) ?: return

        val currentIndex = songwritersnotebook.app.utils.ChordUtils.ALL_NOTES.indexOf(currentNote)
        if (currentIndex == -1) return

        val newIndex = (currentIndex + step).coerceIn(0, songwritersnotebook.app.utils.ChordUtils.ALL_NOTES.size - 1)
        val newNote = songwritersnotebook.app.utils.ChordUtils.ALL_NOTES[newIndex]
        currentTuning[stringIndex] = newNote

        val newTuningList = currentTuning.toList()
        
        val recalculatedNotes = songwritersnotebook.app.utils.ChordUtils.tabToNotes(
            currentState.currentTab,
            newTuningList,
            0 
        )
        
        _uiState.update {
            it.copy(
                guitarTuning = newTuningList,
                manualKeys = recalculatedNotes
            )
        }
        checkIfChordIsFavorited()
        
        currentState.song?.let { song ->
            updateSong(song.copy(guitarTuning = newTuningList), metadataChanged = true)
        }
        
        viewModelScope.launch {
            audioHandler.playNote(newNote, "guitar")
        }
    }

    fun onCapoChange(newCapo: Int) {
        val capo = newCapo.coerceIn(0, 12)
        val currentSong = _uiState.value.song ?: return
        
        _uiState.update { currentState ->
            val oldCapo = currentState.guitarCapo
            val capoDelta = capo - oldCapo
            if (capoDelta == 0) return@update currentState

            val currentTuning = currentState.guitarTuning
            val currentIsCustom = currentState.isCustom
            val currentRoot = currentState.selectedChordRoot
            val currentQuality = currentState.selectedChordQuality
            val originalTab = currentState.currentTab

            val newFretWindowStart = (capo - 1).coerceAtLeast(0)
            var newAbsoluteTab: List<Any>
            var newManualKeys: List<String>
            var newIsCustom: Boolean
            var newRoot: String
            var newQuality: String

            if (!currentIsCustom && currentRoot.isNotBlank()) {
                val relativeTabShape = songwritersnotebook.app.utils.ChordUtils.getTabForCapo(currentRoot, currentQuality, capo)
                if (relativeTabShape != null) {
                    newAbsoluteTab = relativeTabShape.map { fret ->
                        when (fret) {
                            is Int -> if (fret > 0) fret + capo else capo
                            else -> fret
                        }
                    }
                    newManualKeys = songwritersnotebook.app.utils.ChordUtils.tabToNotes(newAbsoluteTab, currentTuning, 0)
                    newIsCustom = false
                    newRoot = currentRoot
                    newQuality = currentQuality
                } else {
                    newAbsoluteTab = listOf('x', 'x', 'x', 'x', 'x', 'x')
                    newManualKeys = emptyList()
                    newIsCustom = true
                    newRoot = ""
                    newQuality = ""
                }
            } else {
                newAbsoluteTab = originalTab.map { fret ->
                    when (fret) {
                        is Int -> {
                            val potentiallyNewAbsoluteFret = fret + capoDelta
                            if (potentiallyNewAbsoluteFret > capo || potentiallyNewAbsoluteFret == 0) {
                                potentiallyNewAbsoluteFret.coerceAtLeast(0)
                            } else {
                                capo
                            }
                        }
                        else -> fret
                    }
                }
                newManualKeys = songwritersnotebook.app.utils.ChordUtils.tabToNotes(newAbsoluteTab, currentTuning, 0)
                newIsCustom = true
                newRoot = ""
                newQuality = ""
            }
            
            currentState.copy(
                guitarCapo = capo,
                currentTab = newAbsoluteTab,
                manualKeys = newManualKeys,
                selectedChordRoot = newRoot,
                selectedChordQuality = newQuality,
                isCustom = newIsCustom,
                fretWindowStart = newFretWindowStart
            )
        }
        checkIfChordIsFavorited()
        updateSong(currentSong.copy(guitarCapo = capo), metadataChanged = true)
    }

    fun resetTuningToStandard() {
        harmonyChangeTuning(songwritersnotebook.app.utils.ChordUtils.DEFAULT_GUITAR_TUNING)
    }

    fun harmonyChangeTuning(tuning: List<String>) {
        if (isViewer) return
        val currentSong = _uiState.value.song ?: return
        
        // Ensure we have exactly 6 notes for a standard guitar
        if (tuning.size != 6) return

        // Process notes to ensure octaves are present
        val processedTuning = tuning.mapIndexed { index, note ->
            val hasOctave = note.any { it.isDigit() }
            if (hasOctave) {
                note // Use provided octave
            } else {
                // Musician's best judgment for standard guitar ranges:
                // String 0 (Lowest): Octave 2
                // String 1: Octave 2
                // String 2: Octave 3
                // String 3: Octave 3
                // String 4: Octave 3
                // String 5 (Highest): Octave 4
                // Special case: if the note is significantly higher/lower, we could adjust, 
                // but this mapping covers Standard, Drop D, Open G, etc.
                when (index) {
                    0, 1 -> "${note}2"
                    2, 3, 4 -> "${note}3"
                    5 -> "${note}4"
                    else -> note
                }
            }
        }

        val currentState = _uiState.value
        val recalculatedNotes = songwritersnotebook.app.utils.ChordUtils.tabToNotes(
            currentState.currentTab,
            processedTuning,
            currentState.guitarCapo
        )
        
        _uiState.update {
            it.copy(
                guitarTuning = processedTuning,
                manualKeys = recalculatedNotes
            )
        }
        
        checkIfChordIsFavorited()
        updateSong(currentSong.copy(guitarTuning = processedTuning), metadataChanged = true)
    }

    fun onCustomToggle() {
        _uiState.update { currentState ->
            val newState = !currentState.isCustom
            if (newState) {
                currentState.copy(
                    isCustom = true,
                    selectedChordRoot = "",
                    selectedChordQuality = ""
                )
            } else {
                val root = currentState.selectedChordRoot.ifBlank { "C" }
                val qualityKey = currentState.selectedChordQuality.ifBlank { "maj" } 
                
                val capo = currentState.guitarCapo
                val targetChordName = songwritersnotebook.app.utils.ChordUtils.getChordName(root, qualityKey)
                val presetPianoNotes = songwritersnotebook.app.utils.ChordUtils.PIANO_CHORD_VOICINGS[targetChordName]
                    ?: songwritersnotebook.app.utils.ChordUtils.calculateChordNotes(root, qualityKey)
                val presetRelativeTab = songwritersnotebook.app.utils.ChordUtils.getTabForCapo(root, qualityKey, capo)
                    ?: listOf('x', 'x', 'x', 'x', 'x', 'x')
                val presetAbsoluteTab = presetRelativeTab.map { fret ->
                    when (fret) {
                        is Int -> if (fret > 0) fret + capo else capo
                        else -> fret
                    }
                }
                currentState.copy(
                    isCustom = false,
                    selectedChordRoot = root,
                    selectedChordQuality = qualityKey,
                    manualKeys = presetPianoNotes,
                    currentTab = presetAbsoluteTab
                )
            }
        }
        checkIfChordIsFavorited()
    }
    // ... (rest of functions) ...
    // --- Deletion ---
    fun confirmChordDeletion() {
        if (isViewer) {
            cancelChordDeletion()
            return
        }
        val chordToDelete = _uiState.value.chordToDelete ?: return
        val currentSong = _uiState.value.song ?: return
        
        val regex = """\[${Regex.escape(chordToDelete)}\]""".toRegex()
        val updatedSections = currentSong.sections.map { section ->
            section.copy(lines = section.lines.map {
                line ->
                line.copy(text = line.text.replace(regex, ""))
            })
        }
        
        val updatedDefinitions = currentSong.chordDefinitions.toMutableMap()
        updatedDefinitions.remove(chordToDelete)
        
        updateSong(currentSong.copy(sections = updatedSections, chordDefinitions = updatedDefinitions), chordLibraryChanged = true)
        
        _uiState.update { 
            it.copy(chordToDelete = null, selectedChordToPlace = if (it.selectedChordToPlace == chordToDelete) null else it.selectedChordToPlace) 
        }
    }

    fun cancelChordDeletion() {
        _uiState.update { it.copy(chordToDelete = null) }
    }

    fun confirmDeletion() {
        val item = _uiState.value.itemToDelete ?: return
        val currentSong = _uiState.value.song ?: return
        
        var deletedSecId: String? = null
        var deletedLinId: String? = null

        val updatedSong = if (item.isMerge && item.lineIndex != null) {
            // "Delete Media" choice: Merge text but drop tabs/recordings
            deletedLinId = item.id
            SongEditorUtils.mergeLineWithPrevious(currentSong, item.sectionIndex, item.lineIndex, item.mergeText ?: "", mergeMedia = false)
        } else if (item.lineIndex != null) {
            deletedLinId = item.id
            SongEditorUtils.deleteLine(currentSong, item.sectionIndex, item.lineIndex)
        } else {
            deletedSecId = item.id
            SongEditorUtils.deleteSection(currentSong, item.sectionIndex)
        }
        
        updateSong(
            updatedSong, 
            sectionIndex = if (item.lineIndex != null) item.sectionIndex else null,
            deletedSectionId = deletedSecId,
            deletedLineId = deletedLinId,
            forceUndoStep = true
        )
        _uiState.update { it.copy(itemToDelete = null, selectedItem = null) }
    }

    fun confirmDeletionWithMerge() {
        val item = _uiState.value.itemToDelete ?: return
        val currentSong = _uiState.value.song ?: return
        if (item.lineIndex == null) return // Section merging not supported in this flow yet
        
        val updatedSong = SongEditorUtils.mergeLineWithPrevious(
            currentSong, 
            item.sectionIndex, 
            item.lineIndex, 
            item.mergeText ?: "", 
            mergeMedia = true
        )
        
        updateSong(
            updatedSong, 
            sectionIndex = item.sectionIndex, 
            deletedLineId = item.id,
            forceUndoStep = true
        )
        _uiState.update { it.copy(itemToDelete = null, selectedItem = null) }
    }

    fun cancelDeletion() {
        _uiState.update { it.copy(itemToDelete = null) }
    }
    
    // --- Media ---
    fun openLineMediaSheet(targetId: String, initialIndex: Int? = null) {
        _uiState.update { it.copy(showLineMediaSheet = true, editingTabTargetId = targetId, initialMediaTabIndex = initialIndex) }
    }

    fun closeLineMediaSheet() {
        stopPlayback()
        _uiState.update { it.copy(showLineMediaSheet = false, editingTabTargetId = null, initialMediaTabIndex = null) }
    }

    fun renameRecording(recording: RecordingMetadata, newLabel: String) {
        if (isViewer) return
        val currentSong = _uiState.value.song ?: return
        val targetId = _uiState.value.editingTabTargetId ?: return
        val currentList = currentSong.recordings[targetId] ?: return
        val updatedList = currentList.map { if (it.id == recording.id) it.copy(label = newLabel) else it }
        val updatedRecordings = currentSong.recordings.toMutableMap().apply { this[targetId] = updatedList }
        updateSong(currentSong.copy(recordings = updatedRecordings))
    }

    /**
     * PRO SELF-HEALING: Triggers a deep repair of the current song.
     */
    fun repairCurrentSong(onComplete: (Boolean) -> Unit) {
        val songId = _uiState.value.song?.id ?: return
        viewModelScope.launch {
            try {
                // Use the private updateIsSyncing helper or direct state update if it was replaced
                val success = songRepository?.repairSong(songId) ?: false
                onComplete(success)
            } catch (e: Exception) {
                onComplete(false)
            }
        }
    }

    fun onStartRecordingFor(targetId: String, idToReplace: String? = null, associatedTabId: String? = null, parentTargetId: String? = null) {
        if (isViewer) return
        val currentSong = _uiState.value.song ?: return
        val recordingToReplace = idToReplace?.let { currentSong.recordings.values.flatten().find { rec -> rec.id == idToReplace } }
        val name = recordingToReplace?.label ?: "New Recording"
        
        // Find context text (Section title or Line text)
        var contextText: String? = null
        val lookupId = parentTargetId ?: targetId
        
        currentSong.sections.forEach { section ->
            if (section.id == lookupId) {
                // For sections, show the title and all lines
                val sb = StringBuilder()
                if (section.title.isNotBlank()) sb.append("${section.title}\n")
                section.lines.forEach { line ->
                    if (line.text.isNotBlank()) sb.append("${line.text}\n")
                }
                contextText = sb.toString().trim()
            } else {
                section.lines.forEach { line ->
                    if (line.id == lookupId) {
                        contextText = line.text
                    }
                }
            }
        }

        // Generate a valid temporary file path for recording
        val appFilesDir = fileSystem.getAppFilesDirectory()
        val recordingsDir = fileSystem.getFile(appFilesDir, "recordings_temp")
        if (!recordingsDir.exists()) {
            val created = recordingsDir.mkdirs()
            println("DEBUG_VM: recordings_temp did not exist. Created: $created")
        } else {
            println("DEBUG_VM: recordings_temp already exists.")
        }
        
        val tempFile = fileSystem.getFile(recordingsDir, "${com.benasher44.uuid.uuid4()}.m4a")
        val outputPath = tempFile.absolutePath
        
        println("DEBUG_VM: onStartRecordingFor: targetId=$targetId, lookupId=$lookupId, text=$contextText")
        
        _uiState.update {
            it.copy(
                showRecordingSheet = true,
                recordingTarget = RecordingTarget(
                    id = targetId, 
                    name = name, 
                    outputFile = outputPath, 
                    idToReplace = idToReplace, 
                    associatedTabId = associatedTabId,
                    parentTargetId = parentTargetId,
                    lineText = contextText
                )
            )
        }
    }

    fun getAudioHandler(): AudioHandler = audioHandler

    fun playRecording(recording: RecordingMetadata) {
        println("DEBUG_VM: playRecording: id=${recording.id}, path='${recording.filePath}'")
        if (_uiState.value.playingRecordingId == recording.id) {
            stopPlayback()
        } else {
            audioHandler.stopPlayback()
            val resolvedPath = songRepository?.getResolvedRecordingUrl(recording.id, recording.filePath) ?: recording.filePath
            println("DEBUG_VM: playRecording resolved path: '$resolvedPath'")
            
            _uiState.update { it.copy(playingRecordingId = recording.id) }
            audioHandler.playAudio(resolvedPath) {
                _uiState.update { if (it.playingRecordingId == recording.id) it.copy(playingRecordingId = null) else it }
            }
        }
    }

    fun stopPlayback() {
        audioHandler.stopPlayback()
        _uiState.update { it.copy(playingRecordingId = null) }
    }
    
    // --- Tab Editor ---
    fun openTabEditor(targetId: String, tabToEdit: GuitarTab? = null) {
        if (currentSubscriptionTier == SubscriptionTier.FREE) {
            _uiState.update { it.copy(showUpgradeDialog = true, upgradeMessage = "Guitar Tabs are a premium feature. Upgrade to unlock them!") }
            return
        }
        _uiState.update {
            it.copy(
                showTabEditorSheet = true,
                editingTab = tabToEdit ?: GuitarTab(name = "New Tab"),
                editingTabTargetId = targetId
            )
        }
    }

    fun dismissTabEditor() {
        val targetId = _uiState.value.editingTabTargetId
        _uiState.update { it.copy(showTabEditorSheet = false, editingTab = null, editingTabTargetId = null) }
    }
    
    fun closeTabEditor() = dismissTabEditor()

    fun saveTab(updatedTab: GuitarTab, dismiss: Boolean = true) {
        if (isViewer) {
            dismissTabEditor()
            return
        }
        val targetId = _uiState.value.editingTabTargetId ?: return
        val currentSong = _uiState.value.song ?: return
        
        fun updateTabList(tabs: List<GuitarTab>): List<GuitarTab> {
            val index = tabs.indexOfFirst { it.id == updatedTab.id }
            return if (index != -1) {
                tabs.toMutableList().apply { set(index, updatedTab) }
            } else {
                tabs + updatedTab
            }
        }

        val updatedSections = currentSong.sections.map { section ->
            if (section.id == targetId) {
                // Update section-level tab
                section.copy(tabs = updateTabList(section.tabs))
            } else {
                // Update line-level tab
                val updatedLines = section.lines.map { line ->
                    if (line.id == targetId) {
                        line.copy(tabs = updateTabList(line.tabs))
                    } else {
                        line
                    }
                }
                section.copy(lines = updatedLines)
            }
        }
        
        // STRUCTURAL CHECK: Compare ignoring the top-level song object properties (like lastModified)
        if (updatedSections == currentSong.sections) {
            println("DEBUG_VM: saveTab detected no changes, skipping update.")
            if (dismiss) dismissTabEditor()
            return
        }
        
        updateSong(currentSong.copy(sections = updatedSections), forceUndoStep = dismiss)
        if (dismiss) dismissTabEditor()
    }

    fun deleteTab(tab: GuitarTab) {
        if (isViewer) return
        val targetId = _uiState.value.editingTabTargetId ?: return
        val currentSong = _uiState.value.song ?: return
        
        val updatedSections = currentSong.sections.map { section ->
            if (section.id == targetId) {
                section.copy(tabs = section.tabs.filter { it.id != tab.id })
            } else {
                val updatedLines = section.lines.map { line ->
                    if (line.id == targetId) {
                        line.copy(tabs = line.tabs.filter { it.id != tab.id })
                    } else {
                        line
                    }
                }
                section.copy(lines = updatedLines)
            }
        }
        updateSong(currentSong.copy(sections = updatedSections), forceUndoStep = true)
    }
    
    // --- Settings ---
    fun toggleShowSectionHeaders() { 
        val newValue = !_uiState.value.editorSettings.showSectionHeaders
        _uiState.update { it.copy(editorSettings = it.editorSettings.copy(showSectionHeaders = newValue)) }
        preferencesManager.showSectionHeaders = newValue
    }
    fun toggleSyllableCount() { 
        val newValue = !_uiState.value.editorSettings.syllableCountEnabled
        _uiState.update { it.copy(editorSettings = it.editorSettings.copy(syllableCountEnabled = newValue)) }
        preferencesManager.syllableCountEnabled = newValue
    }
    fun toggleRhyming() {
        val newValue = !_uiState.value.editorSettings.rhymingEnabled
        val newSettings = _uiState.value.editorSettings.copy(rhymingEnabled = newValue)
        _uiState.update { it.copy(editorSettings = newSettings) }
        preferencesManager.rhymingEnabled = newValue
        findRhymes(_uiState.value.song, newSettings, immediate = true)
    }
    fun toggleSlantRhymes() {
        val newValue = !_uiState.value.editorSettings.showSlantRhymes
        val newSettings = _uiState.value.editorSettings.copy(showSlantRhymes = newValue)
        _uiState.update { it.copy(editorSettings = newSettings) }
        preferencesManager.showSlantRhymes = newValue
        findRhymes(_uiState.value.song, newSettings, immediate = true)
    }
    fun toggleEndOfLineOnly() {
        val newValue = !_uiState.value.editorSettings.endOfLineOnly
        val newSettings = _uiState.value.editorSettings.copy(endOfLineOnly = newValue)
        _uiState.update { it.copy(editorSettings = newSettings) }
        preferencesManager.endOfLineOnly = newValue
        findRhymes(_uiState.value.song, newSettings, immediate = true)
    }
    fun toggleShowPianoInfo() { 
        val songId = _uiState.value.song?.id ?: return
        val newValue = !_uiState.value.editorSettings.showPianoInfo
        preferencesManager.setShowPianoInfo(songId, newValue)
        _uiState.update { it.copy(editorSettings = it.editorSettings.copy(showPianoInfo = newValue)) } 
    }
    fun toggleShowGuitarInfo() { 
        val songId = _uiState.value.song?.id ?: return
        val newValue = !_uiState.value.editorSettings.showGuitarInfo
        preferencesManager.setShowGuitarInfo(songId, newValue)
        _uiState.update { it.copy(editorSettings = it.editorSettings.copy(showGuitarInfo = newValue)) } 
    }

    fun updateNotes(notes: String) {
        if (isViewer) return
        _uiState.value.song?.let { song ->
            val oldText = song.notes
            val newText = if (notes.length > 5000) notes.substring(0, 5000) else notes
            if (oldText == newText) return@let

            // PRO COLLABORATION: Atomic Diffing
            var prefixLen = 0
            while (prefixLen < oldText.length && prefixLen < newText.length && oldText[prefixLen] == newText[prefixLen]) {
                prefixLen++
            }

            var suffixLen = 0
            while (suffixLen < oldText.length - prefixLen && 
                   suffixLen < newText.length - prefixLen && 
                   oldText[oldText.length - 1 - suffixLen] == newText[newText.length - 1 - suffixLen]) {
                suffixLen++
            }

            val deleteCount = oldText.length - prefixLen - suffixLen
            val insertText = newText.substring(prefixLen, newText.length - suffixLen)

            // 1. Update Local UI immediately AND synchronously (Main Thread)
            val updatedSong = song.copy(notes = newText, lastModified = Clock.System.now())
            _uiState.update { it.copy(song = updatedSong) }
            hasUnsavedChanges.value = true
            hasSessionEdits = true

            // 2. Push Atomic Delta in background
            viewModelScope.launch(Dispatchers.Default) {
                songRepository?.pushSongNotesUpdate(
                    songId = song.id,
                    index = prefixLen,
                    deleteCount = deleteCount,
                    insertText = insertText,
                    isShared = isCollaborationEnabled
                )
            }
            triggerDebouncedSave()
            lastKeystrokeTime = Clock.System.now().toEpochMilliseconds()
        }
    }
    fun toggleShowNotes() {
        if (isViewer) return
        val newValue = !(_uiState.value.song?.showNotes ?: false)
        _uiState.value.song?.let { song -> 
            updateSong(song.copy(showNotes = newValue), metadataChanged = true) 
        }
    }

    fun setChordDisplayMode(mode: ChordDisplayMode) {
        _uiState.update { it.copy(editorSettings = it.editorSettings.copy(chordDisplay = mode)) }
        preferencesManager.chordDisplayMode = mode.name
    }

    fun setChordPlacement(placement: ChordPlacement) {
        _uiState.update { it.copy(editorSettings = it.editorSettings.copy(chordPlacement = placement)) }
    }

    fun toggleShowTabs() {
        if (currentSubscriptionTier == SubscriptionTier.FREE) {
            _uiState.update { it.copy(showUpgradeDialog = true, upgradeMessage = "Guitar Tabs are a premium feature. Upgrade to unlock them!") }
            return
        }
        val newValue = !_uiState.value.editorSettings.showTabs
        _uiState.update { it.copy(editorSettings = it.editorSettings.copy(showTabs = newValue)) }
        preferencesManager.showTabs = newValue
    }
    
    // --- Word Finder ---
    fun openWordFinderPopup() {
        _uiState.update { it.copy(showWordFinderPopup = true) }
    }

    fun closeWordFinderPopup() {
        _uiState.update { it.copy(showWordFinderPopup = false) }
    }

    fun updateWordFinderSearchTerm(term: String) {
        _uiState.update { it.copy(wordFinderSearchTerm = term, wordFinderError = null, rhymeResults = emptyList(), synonymResults = emptyList()) }
    }

    fun selectWordFinderTab(tab: WordFinderTab) {
        _uiState.update { it.copy(wordFinderSelectedTab = tab) }
    }

    fun findWords() {
        val term = _uiState.value.wordFinderSearchTerm.trim()
        if (term.isBlank()) {
            _uiState.update { it.copy(wordFinderError = "Please enter a word to search.") }
            return
        }
        _uiState.update { it.copy(isWordFinderLoading = true, wordFinderError = null, rhymeResults = emptyList(), synonymResults = emptyList()) }
        viewModelScope.launch {
            val rhymeJob = async { database?.let { songwritersnotebook.app.utils.RhymeService.fetchRhymesForWord(it, term) } }
            val synonymJob = async { thesaurusService.getSynonyms(term) }
            val rhymeApiResults = try { rhymeJob.await() } catch (e: Exception) { null }
            val synonymApiResults = try { synonymJob.await() } catch (e: Exception) { null }
            _uiState.update { currentState ->
                currentState.copy(
                    rhymeResults = rhymeApiResults ?: emptyList(),
                    synonymResults = synonymApiResults ?: emptyList(),
                    wordFinderError = if (rhymeApiResults == null && synonymApiResults == null) "Error fetching results." else null,
                    isWordFinderLoading = false
                )
            }
        }
    }
    
    // --- AI ---
    fun openAiAssistant() {
        _uiState.update { it.copy(showAiAssistantSheet = true) }
    }

    fun closeAiAssistant() {
        _uiState.update { it.copy(showAiAssistantSheet = false) }
    }

    fun onAiAssistantClicked() {
        if (isViewer) return
        
        if (_uiState.value.isHarmonyListening) {
            // If already listening (from a previous hold or toggle), tap to stop and send
            completeHarmonyAudioRequest()
        } else {
            // Otherwise, open the text panel
            _uiState.update { it.copy(showAiAssistantSheet = true) }
        }
    }
    
    private var harmonyListeningJob: kotlinx.coroutines.Job? = null
    private var silenceDetectionJob: kotlinx.coroutines.Job? = null
    private var harmonyListeningStartTimeMs = 0L

    fun onAiAssistantHold() {
        if (isViewer) return
        
        if (!networkUtils.isNetworkAvailable()) {
            _uiState.update { it.copy(cloudSyncError = "No network connection. AI features require an internet connection.", showAiAssistantSheet = false) }
            return
        }

        val totalCredits = _uiState.value.userProfile?.totalAiCredits ?: 0
        if (totalCredits <= 0) {
            _uiState.update { it.copy(showAiLimitDialog = true, showAiAssistantSheet = false) }
            return
        }
        
        // Stop any current speaking when Harmony starts listening
        ttsHelper?.stop()

        // Setup a temporary file path for the audio buffer
        val appFilesDir = fileSystem.getAppFilesDirectory()
        val tempFile = fileSystem.getFile(appFilesDir, "harmony_temp.m4a")
        
        // Start listening
        _uiState.update { it.copy(isHarmonyListening = true, harmonyTranscript = "Listening...") }
        harmonyListeningStartTimeMs = kotlinx.datetime.Clock.System.now().toEpochMilliseconds()
        
        viewModelScope.launch {
            audioHandler.startRecording(tempFile.absolutePath)
        }

        // 1. Safety timeout (60 seconds max)
        harmonyListeningJob?.cancel()
        harmonyListeningJob = viewModelScope.launch {
            kotlinx.coroutines.delay(30000)
            if (_uiState.value.isHarmonyListening) {
                println("DEBUG_HARMONY: Safety timeout reached. Completing request.")
                completeHarmonyAudioRequest()
            }
        }

        // 2. Silence detection (Auto-send if silent for 1.5 seconds)
        silenceDetectionJob?.cancel()
        silenceDetectionJob = viewModelScope.launch {
            var silenceDurationMs = 0L
            val pollIntervalMs = 200L
            val silenceThreshold = 2500 // Increased threshold for realistic background noise
            val requiredSilenceMs = 1500L // 1.5 seconds of silence to auto-send

            while (_uiState.value.isHarmonyListening) {
                kotlinx.coroutines.delay(pollIntervalMs)
                val amplitude = audioHandler.getMaxAmplitude()
                
                if (amplitude < silenceThreshold) {
                    silenceDurationMs += pollIntervalMs
                } else {
                    silenceDurationMs = 0 // Reset on sound
                }

                if (silenceDurationMs >= requiredSilenceMs) {
                    println("DEBUG_HARMONY: Silence detected ($silenceDurationMs ms). Completing request.")
                    completeHarmonyAudioRequest()
                    break
                }
            }
        }
    }
    
    private fun completeHarmonyAudioRequest() {
        if (!_uiState.value.isHarmonyListening) return
        
        val now = kotlinx.datetime.Clock.System.now().toEpochMilliseconds()
        if (now - harmonyListeningStartTimeMs < 800) {
            println("DEBUG_HARMONY: Ignoring stop request, recording too short.")
            return
        }
        
        harmonyListeningJob?.cancel()
        silenceDetectionJob?.cancel()
        val effectiveUserIdForMsg = effectiveUserIdForChat
        val userMessage = songwritersnotebook.app.data.HarmonyChatMessage(text = "🎙️ Audio Message", isUser = true, userId = effectiveUserIdForMsg)
        
        _uiState.update { 
            it.copy(
                isHarmonyListening = false, 
                isHarmonyThinking = true, 
                harmonyTranscript = "",
                harmonyChatHistory = it.harmonyChatHistory + HarmonyChatMessage(text = userMessage.text, isUser = userMessage.isUser, id = userMessage.id)
            ) 
        }
        
        // Persist to Song Repository
        _uiState.value.song?.let { currentSong ->
            viewModelScope.launch {
                songRepository?.saveHarmonyChatMessage(currentSong.id, userMessage)
            }
        }
        
        // Send request to Firebase
        val appFilesDir = fileSystem.getAppFilesDirectory()
        val tempFile = fileSystem.getFile(appFilesDir, "harmony_temp.m4a")
        
        viewModelScope.launch {
            audioHandler.stopRecording() // Suspend until file is fully written
            processHarmonyRequest(audioFilePath = tempFile.absolutePath)
        }
    }
    
    fun onAiAssistantRelease() {
        if (!_uiState.value.isHarmonyListening) return
        println("DEBUG_HARMONY: Button released. Waiting for silence to complete...")
    }
    
    fun onAiAssistantToggleListening() {
        // Stop any current speaking when user interacts with mic
        ttsHelper?.stop()

        if (_uiState.value.isHarmonyListening) {
            completeHarmonyAudioRequest()
        } else {
            onAiAssistantHold()
        }
    }

    fun onClearChatRequest() {
        _uiState.update { it.copy(showClearChatDialog = true) }
    }

    fun onClearChatDismiss() {
        _uiState.update { it.copy(showClearChatDialog = false) }
    }

    fun clearHarmonyChatHistory() {
        val effectiveUserIdToClear = effectiveUserIdForChat
        _uiState.update { it.copy(harmonyChatHistory = emptyList(), showClearChatDialog = false) }
        _uiState.value.song?.let { currentSong ->
            viewModelScope.launch {
                songRepository?.clearHarmonyChatHistory(currentSong.id, effectiveUserIdToClear)
            }
        }
    }
    
    fun loadMoreHarmonyChatHistory() {
        val currentSong = _uiState.value.song ?: return
        val currentSize = _uiState.value.harmonyChatHistory.size
        // If we have less than 50, we probably haven't even hit the first limit
        if (currentSize < 50) return
        
        val effectiveUserId = effectiveUserIdForChat
        viewModelScope.launch {
            // Fetch next page
            val newLimit = currentSize + 50
            val history = songRepository?.getHarmonyChatHistory(currentSong.id, effectiveUserId, limit = newLimit.toLong())?.reversed() ?: emptyList()
            
            _uiState.update { it.copy(
                harmonyChatHistory = history.map { msg -> HarmonyChatMessage(text = msg.text, isUser = msg.isUser, id = msg.id) }
            ) }
        }
    }
    
    fun sendHarmonyTextMessage(textPrompt: String) {
        if (textPrompt.isBlank()) return

        if (!networkUtils.isNetworkAvailable()) {
            _uiState.update { it.copy(cloudSyncError = "No network connection. AI features require an internet connection.") }
            return
        }

        val effectiveUserIdForMsg = effectiveUserIdForChat
        val userMessage = songwritersnotebook.app.data.HarmonyChatMessage(text = textPrompt, isUser = true, userId = effectiveUserIdForMsg)
        _uiState.update { 
            it.copy(
                isHarmonyThinking = true, 
                harmonyTranscript = textPrompt, 
                harmonySpokenResponse = null,
                harmonyChatHistory = it.harmonyChatHistory + HarmonyChatMessage(text = userMessage.text, isUser = userMessage.isUser, id = userMessage.id)
            ) 
        }
        
        // Persist to Song Repository
        _uiState.value.song?.let { currentSong ->
            viewModelScope.launch {
                songRepository?.saveHarmonyChatMessage(currentSong.id, userMessage)
            }
        }
        
        viewModelScope.launch {
            processHarmonyRequest(audioFilePath = null, textPrompt = textPrompt)
        }
    }
    
    fun onAiAssistantCancel() {
        if (!_uiState.value.isHarmonyListening) return
        
        ttsHelper?.stop()
        
        audioHandler.stopRecordingNonBlocking()
        _uiState.update { it.copy(isHarmonyListening = false, isHarmonyThinking = false, harmonyTranscript = "") }
    }
    
    private suspend fun processHarmonyRequest(audioFilePath: String?, textPrompt: String? = null) {
        val user = songwritersnotebook.app.utils.AuthHandler().getCurrentUser()
        if (user == null) {
            _uiState.update { it.copy(isHarmonyThinking = false, harmonySpokenResponse = "Please sign in to use Harmony.") }
            return
        }
        
        val currentSong = _uiState.value.song
        if (currentSong == null) {
            _uiState.update { it.copy(isHarmonyThinking = false) }
            return
        }
        
        var base64Audio: String? = null
        if (audioFilePath != null) {
            try {
                // Wait briefly for file to be completely written by audio framework
                kotlinx.coroutines.delay(300) 
                
                val file = fileSystem.getFile(audioFilePath)
                if (file.exists() && file.length() > 0) {
                    val bytes = file.readBytes()
                    @OptIn(kotlin.io.encoding.ExperimentalEncodingApi::class)
                    base64Audio = kotlin.io.encoding.Base64.encode(bytes)
                }
            } catch (e: Exception) {
                println("Failed to read harmony audio: ${e.message}")
            }
        }
        
        if (base64Audio == null && textPrompt == null) {
            _uiState.update { it.copy(isHarmonyThinking = false, harmonySpokenResponse = "I couldn't hear anything.") }
            return
        }
        
        // Fetch ID token
        val idToken = dev.gitlive.firebase.Firebase.auth.currentUser?.getIdToken(false) ?: ""
        
        println("DEBUG_HARMONY: Sending request to server. Prompt: ${textPrompt ?: "[Audio]"}, AudioSize: ${base64Audio?.length ?: 0}")

        val response = try {
            kotlinx.coroutines.withTimeout(30_000) {
                val recentMessagesContext = _uiState.value.harmonyChatHistory.takeLast(10).map { 
                    songwritersnotebook.app.data.HarmonyChatMessage(text = it.text, isUser = it.isUser, id = it.id, userId = effectiveUserIdForChat)
                }
                
                // Sanitize the song state to avoid sending massive useless binary data (CRDT/Presence) to the LLM
                val sanitizedSongState = currentSong.copy(
                    harmonyChatHistory = recentMessagesContext,
                    crdtState = null,
                    heads = emptyList(),
                    presence = emptyMap()
                )
                
                aiHarmonyService.askHarmony(
                    prompt = textPrompt ?: "",
                    audioData = base64Audio,
                    audioMimeType = "audio/mp4",
                    songState = sanitizedSongState,
                    libraryChords = currentSong.chordDefinitions.values.toList(),
                    guitarTuning = currentSong.guitarTuning,
                    guitarCapo = currentSong.guitarCapo,
                    idToken = idToken,
                    confirmationRequired = _uiState.value.editorSettings.harmonyConfirmationRequired
                )
            }
        } catch (e: kotlinx.coroutines.TimeoutCancellationException) {
            println("DEBUG_HARMONY: Request timed out after 30s")
            _uiState.update { it.copy(isHarmonyThinking = false, harmonySpokenResponse = "Harmony is taking too long to respond. She may be starting up — try again in a moment.") }
            // Add error as chat bubble so it's visible in history
            val errorMsg = songwritersnotebook.app.data.HarmonyChatMessage(text = "⚠️ Harmony timed out. Please try again in a moment.", isUser = false, userId = effectiveUserIdForChat)
            _uiState.update { it.copy(harmonyChatHistory = it.harmonyChatHistory + HarmonyChatMessage(text = errorMsg.text, isUser = false, id = errorMsg.id)) }
            return
        } catch (e: Exception) {
            val isNetworkError = e.message?.let { msg ->
                msg.contains("UnknownHostException", ignoreCase = true) ||
                msg.contains("ConnectException", ignoreCase = true) ||
                msg.contains("SocketException", ignoreCase = true) ||
                msg.contains("Network", ignoreCase = true) ||
                msg.contains("ECONNREFUSED", ignoreCase = true)
            } ?: false
            
            val friendlyMessage = if (isNetworkError) {
                "Harmony couldn't connect. Check your internet connection and try again."
            } else {
                "Harmony is unavailable right now. Please try again in a moment."
            }
            println("DEBUG_HARMONY: Request failed: ${e.message}")
            _uiState.update { it.copy(isHarmonyThinking = false, harmonySpokenResponse = friendlyMessage) }
            val errorMsg = songwritersnotebook.app.data.HarmonyChatMessage(text = "⚠️ $friendlyMessage", isUser = false, userId = effectiveUserIdForChat)
            _uiState.update { it.copy(harmonyChatHistory = it.harmonyChatHistory + HarmonyChatMessage(text = errorMsg.text, isUser = false, id = errorMsg.id)) }
            return
        }
        
        _uiState.update { it.copy(isHarmonyThinking = false) }
        
        println("DEBUG_HARMONY: Received response. Status: ${response.status}, Transcript: ${response.transcript}, ToolCalls: ${response.toolCalls.size}, Text: ${response.textResponse}")

        if (response.status == "error") {
            val isOffline = response.message?.let { msg ->
                msg.contains("UNAVAILABLE", ignoreCase = true) ||
                msg.contains("503", ignoreCase = true) ||
                msg.contains("network", ignoreCase = true)
            } ?: false
            val friendlyError = if (isOffline) "Harmony is offline right now. Please try again in a moment." else (response.message ?: "An error occurred.")
            val errorMsg = songwritersnotebook.app.data.HarmonyChatMessage(text = "⚠️ $friendlyError", isUser = false, userId = effectiveUserIdForChat)
            _uiState.update { it.copy(
                harmonySpokenResponse = friendlyError,
                harmonyChatHistory = it.harmonyChatHistory + HarmonyChatMessage(text = errorMsg.text, isUser = false, id = errorMsg.id)
            ) }
            return
        }

        // 1. Update Transcript if available
        if (!response.transcript.isNullOrBlank() && audioFilePath != null) {
            var updatedMessage: songwritersnotebook.app.data.HarmonyChatMessage? = null
            
            _uiState.update { state ->
                val updatedHistory = state.harmonyChatHistory.toMutableList()
                val lastIdx = updatedHistory.indexOfLast { it.isUser && it.text == "🎙️ Audio Message" }
                if (lastIdx != -1) {
                    updatedHistory[lastIdx] = updatedHistory[lastIdx].copy(text = response.transcript)
                    updatedMessage = songwritersnotebook.app.data.HarmonyChatMessage(
                        id = updatedHistory[lastIdx].id,
                        text = updatedHistory[lastIdx].text,
                        isUser = true,
                        userId = effectiveUserIdForChat
                    )
                }
                state.copy(harmonyChatHistory = updatedHistory)
            }
            
            // Persist to Song Repository
            if (updatedMessage != null) {
                _uiState.value.song?.let { currentSong ->
                    viewModelScope.launch {
                        songRepository?.saveHarmonyChatMessage(currentSong.id, updatedMessage!!)
                    }
                }
            }
        }
        
        val safeTools = setOf(
            "insert_chord_into_lyrics",
            "add_lyric_line",
            "update_lyric_line",
            "rename_recording",
            "transpose_song",
            "get_rhymes",
            "play_chord",
            "navigate_to_studio",
            "open_word_finder",
            "toggle_metronome",
            "set_metronome_mute",
            "change_setting",
            "add_section",
            "move_item",
            "duplicate_item",
            "set_metronome",
            "set_instrument",
            "save_song",
            "split_line",
            "merge_line_with_previous",
            "merge_section_with_previous",
            "set_chord_display_mode",
            "toggle_show_notes",
            "update_notes",
            "play_recording",
            "stop_playback",
            "open_media_for_line",
            "add_guitar_tab",
            "update_guitar_tab",
            "start_recording", 
            "change_bpm", 
            "rename_section", 
            "update_song_title", 
            "change_capo", 
            "reset_tuning_to_standard"
        )
        
        val destructiveTools = setOf(
            "delete_section",
            "delete_lyric_line",
            "remove_chord",
            "delete_item",
            "delete_recording",
            "remove_chord_from_library"
        )
        
        if (response.toolCalls.isNotEmpty()) {
            hasUnsavedChanges.value = true
            
            val (autoExecute, manualConfirm) = response.toolCalls.partition { call ->
                // Safe tools can auto-execute IF user settings allow it (or if it's always safe)
                // BUT Destructive tools ALWAYS require confirmation regardless of settings.
                !destructiveTools.contains(call.name) && 
                (safeTools.contains(call.name) || !_uiState.value.editorSettings.harmonyConfirmationRequired)
            }

            // 1. Execute safe tools immediately
            autoExecute.forEach { call ->
                println("DEBUG_HARMONY: Executing tool call: ${call.name}")
                harmonyActionExecutor.execute(call.name, call.args)
            }

            // 2. Buffer destructive actions for UI confirmation
            if (manualConfirm.isNotEmpty()) {
                val pendingActions = manualConfirm.map { 
                    songwritersnotebook.app.viewmodels.HarmonyPendingAction(it.name, it.args) 
                }
                _uiState.update { it.copy(harmonyPendingActions = it.harmonyPendingActions + pendingActions) }
            }
        }
        
        if (!response.textResponse.isNullOrBlank()) {
            val aiMessageData = songwritersnotebook.app.data.HarmonyChatMessage(text = response.textResponse, isUser = false, userId = effectiveUserIdForChat)
            val aiMessageUi = HarmonyChatMessage(text = aiMessageData.text, isUser = aiMessageData.isUser, id = aiMessageData.id)
            
            _uiState.update { 
                it.copy(
                    harmonySpokenResponse = response.textResponse,
                    harmonyChatHistory = it.harmonyChatHistory + aiMessageUi
                ) 
            }
            
            // Persist to Song Repository
            _uiState.value.song?.let { currentSong ->
                viewModelScope.launch {
                    songRepository?.saveHarmonyChatMessage(currentSong.id, aiMessageData)
                }
            }
            
            // Trigger TTS here
            if (_uiState.value.harmonyPendingActions.isEmpty()) {
                speakHarmonyResponse(response.textResponse) {
                    if (_uiState.value.harmonySpokenResponse == response.textResponse) {
                        _uiState.update { it.copy(harmonySpokenResponse = null) }
                    }
                }
            } else {
                speakHarmonyResponse(response.textResponse)
            }
        } else if (response.toolCalls.isNotEmpty()) {
            val fallbackText = "I've prepared that update for you."
            val aiMessageData = songwritersnotebook.app.data.HarmonyChatMessage(text = fallbackText, isUser = false, userId = effectiveUserIdForChat)
            val aiMessageUi = HarmonyChatMessage(text = aiMessageData.text, isUser = aiMessageData.isUser, id = aiMessageData.id)
            
            _uiState.update { 
                it.copy(
                    harmonySpokenResponse = fallbackText,
                    harmonyChatHistory = it.harmonyChatHistory + aiMessageUi
                ) 
            }
            
            // Persist to Song Repository
            _uiState.value.song?.let { currentSong ->
                viewModelScope.launch {
                    songRepository?.saveHarmonyChatMessage(currentSong.id, aiMessageData)
                }
            }
            
            if (_uiState.value.harmonyPendingActions.isEmpty()) {
                speakHarmonyResponse(fallbackText) {
                    if (_uiState.value.harmonySpokenResponse == fallbackText) {
                        _uiState.update { it.copy(harmonySpokenResponse = null) }
                    }
                }
            } else {
                speakHarmonyResponse(fallbackText)
            }
        }
    }
    
    private fun speakHarmonyResponse(text: String, onDone: (() -> Unit)? = null) {
        if (!_uiState.value.editorSettings.harmonyVoiceEnabled) {
            onDone?.invoke()
            return
        }
        
        if (ttsHelper != null) {
            ttsHelper.speak(text, onStart = null, onDone = onDone)
        } else {
            println("TTS not available on this platform")
            if (onDone != null) {
                val delayTime = (text.length * 70L).coerceAtLeast(3000L).coerceAtMost(8000L)
                viewModelScope.launch {
                    kotlinx.coroutines.delay(delayTime)
                    onDone()
                }
            }
        }
    }
    
    fun toggleHarmonyVoiceEnabled() {
        val newValue = !_uiState.value.editorSettings.harmonyVoiceEnabled
        _uiState.update { it.copy(editorSettings = it.editorSettings.copy(harmonyVoiceEnabled = newValue)) }
        preferencesManager.harmonyVoiceEnabled = newValue
    }
    
    fun toggleAiSheetHeight() {
        _uiState.update { it.copy(isAiSheetFullHeight = !it.isAiSheetFullHeight) }
    }

    fun dismissHarmonyOverlay() {
        ttsHelper?.stop()
        audioHandler.stopRecordingNonBlocking()
        
        val pendingActions = _uiState.value.harmonyPendingActions
        pendingActions.forEach { onHarmonyActionReject(it.id) }
        
        _uiState.update { 
            it.copy(
                isHarmonyListening = false,
                isHarmonyThinking = false,
                harmonySpokenResponse = null,
                harmonyPendingActions = emptyList(),
                harmonyTranscript = ""
            ) 
        }
    }

    fun onAiAssistantDismiss() {
        ttsHelper?.stop()
        stopMetronome()

        _uiState.update {
            it.copy(
                showAiAssistantSheet = false,
                isHarmonyListening = false,
                isHarmonyThinking = false,
                harmonySpokenResponse = null
            )
        }
    }

    fun onEditorSettingsDismissed() {
        stopTempoPreview()
        saveSong()
    }

    fun toggleTempoPreview() {
        val newValue = !_uiState.value.isTempoPreviewActive
        _uiState.update { it.copy(isTempoPreviewActive = newValue) }
        
        if (newValue) {
            val bpm = _uiState.value.song?.bpm ?: 120
            audioHandler.startMetronome(bpm) { beat ->
                viewModelScope.launch { _uiState.update { it.copy(currentMetronomeBeat = beat) } }
            }
        } else {
            stopTempoPreview()
        }
    }

    fun stopTempoPreview() {
        audioHandler.stopMetronome()
        _uiState.update { 
            it.copy(
                isTempoPreviewActive = false,
                currentMetronomeBeat = -1
            ) 
        }
    }

    fun stopMetronome() {
        audioHandler.stopMetronome()
        _uiState.update { 
            it.copy(
                isMetronomeEnabled = false,
                currentMetronomeBeat = -1
            ) 
        }
    }

    fun selectAiAssistantTab(index: Int) {
        _uiState.update { it.copy(aiAssistantSelectedTab = index) }
    }    fun notifyTyping() { hasUnsavedChanges.value = true }
    fun onAiLimitDialogDismiss() { _uiState.update { it.copy(showAiLimitDialog = false) } }
    fun onSignInPromptDismissed() { _uiState.update { it.copy(showSignInPrompt = false) } }
    fun onQuotaExceededDialogDismiss() { _uiState.update { it.copy(showQuotaExceededDialog = false) } }
    
    private fun refreshUserProfile() {
        viewModelScope.launch {
            songRepository?.getCurrentUserProfile()
        }
    }


    fun requestRewardedAiUses(context: Any) {
        val rewardedAdManager = PlatformDependencyProvider().getRewardedAdManager()
        if (rewardedAdManager != null) {
             _uiState.update { it.copy(showAiLimitDialog = false) }
             rewardedAdManager.showAd(
                 activity = context,
                 onRewardGained = { amount ->
                     viewModelScope.launch {
                         try {
                             // Use local refresh since we don't have the MainViewModel reference here
                             songwritersnotebook.app.utils.CloudFunctionService.awardAdCredits()
                             refreshUserProfile()
                         } catch (e: Exception) {
                             println("DEBUG_REPO: Error awarding ad credits: $e")
                             songwritersnotebook.app.utils.PlatformToast.show("Failed to award credits. Please check your connection or app version.")
                         }
                     }
                 },
                 onAdFailed = {
                     // Ad failed
                 }
             )
        } else {
            println("RewardedAdManager not available")
        }
    }
    fun triggerAiLimitDialog() {
        _uiState.update { it.copy(showAiLimitDialog = true, showAiAssistantSheet = false) }
    }
    
    // --- Harmony Actions ---
    
    fun insertHarmonyChord(sectionId: String, lineIndex: Int, wordIndex: Int, chordName: String) {
        val currentSong = _uiState.value.song ?: return
        val sectionIndex = currentSong.sections.indexOfFirst { it.id == sectionId }
        if (sectionIndex == -1) return
        
        val section = currentSong.sections[sectionIndex]
        if (lineIndex < 0 || lineIndex >= section.lines.size) return
        val targetLine = section.lines[lineIndex]
        
        // Find safe insertion index (cannot exceed string length)
        val safeIndex = wordIndex.coerceIn(0, targetLine.text.length)
        
        val left = targetLine.text.substring(0, safeIndex)
        val right = targetLine.text.substring(safeIndex)
        
        val newText = "$left[$chordName]$right"
        val newLine = targetLine.copy(text = newText)
        
        val newLines = section.lines.toMutableList()
        newLines[lineIndex] = newLine
        
        val newSections = currentSong.sections.toMutableList()
        newSections[sectionIndex] = section.copy(lines = newLines)
        
        updateSong(currentSong.copy(sections = newSections))
    }
    
    fun addHarmonyChordToLibrary(name: String, pianoNotes: List<String>, guitarTab: List<String>) {
        val currentSong = _uiState.value.song ?: return
        val newDefinition = songwritersnotebook.app.data.ChordDefinition(
            name = name,
            pianoNotes = pianoNotes,
            guitarTab = guitarTab,
            isCustom = true
        )
        val updatedDefinitions = currentSong.chordDefinitions.toMutableMap()
        updatedDefinitions[name] = newDefinition
        updateSong(currentSong.copy(chordDefinitions = updatedDefinitions))
    }
    
    fun addHarmonyLyricLine(sectionId: String, text: String) {
        val currentSong = _uiState.value.song ?: return
        val sectionIndex = currentSong.sections.indexOfFirst { it.id == sectionId }
        if (sectionIndex == -1) return
        
        val section = currentSong.sections[sectionIndex]
        val newLine = songwritersnotebook.app.data.Line(text = text)
        
        val newSections = currentSong.sections.toMutableList()
        newSections[sectionIndex] = section.copy(lines = section.lines + newLine)
        
        updateSong(currentSong.copy(sections = newSections))
    }
    
    fun harmonyDeleteSection(sectionId: String) {
        val currentSong = _uiState.value.song ?: return
        val sectionIndex = currentSong.sections.indexOfFirst { it.id == sectionId }
        if (sectionIndex == -1) return
        val updatedSong = songwritersnotebook.app.utils.SongEditorUtils.deleteSection(currentSong, sectionIndex)
        updateSong(updatedSong)
    }

    fun harmonyDeleteLyricLine(sectionId: String, lineIndex: Int) {
        val currentSong = _uiState.value.song ?: return
        val sectionIndex = currentSong.sections.indexOfFirst { it.id == sectionId }
        if (sectionIndex == -1) return
        val updatedSong = songwritersnotebook.app.utils.SongEditorUtils.deleteLine(currentSong, sectionIndex, lineIndex)
        updateSong(updatedSong)
    }

    fun harmonyUpdateLyricLine(sectionId: String, lineIndex: Int, text: String) {
        val currentSong = _uiState.value.song ?: return
        val sectionIndex = currentSong.sections.indexOfFirst { it.id == sectionId }
        if (sectionIndex == -1) return
        val section = currentSong.sections[sectionIndex]
        if (lineIndex < 0 || lineIndex >= section.lines.size) return
        
        val newLine = section.lines[lineIndex].copy(text = text)
        val newLines = section.lines.toMutableList()
        newLines[lineIndex] = newLine
        
        val newSections = currentSong.sections.toMutableList()
        newSections[sectionIndex] = section.copy(lines = newLines)
        updateSong(currentSong.copy(sections = newSections))
    }

    fun harmonyRemoveChord(sectionId: String, lineIndex: Int, chordName: String) {
        val currentSong = _uiState.value.song ?: return
        val sectionIndex = currentSong.sections.indexOfFirst { it.id == sectionId }
        if (sectionIndex == -1) return
        
        val section = currentSong.sections[sectionIndex]
        if (lineIndex < 0 || lineIndex >= section.lines.size) return
        val targetLine = section.lines[lineIndex]
        
        val regex = """\[${Regex.escape(chordName)}\]""".toRegex()
        val newLine = targetLine.copy(text = targetLine.text.replaceFirst(regex, ""))
        
        val newLines = section.lines.toMutableList()
        newLines[lineIndex] = newLine
        
        val newSections = currentSong.sections.toMutableList()
        newSections[sectionIndex] = section.copy(lines = newLines)
        updateSong(currentSong.copy(sections = newSections))
    }

    fun harmonyRenameSection(sectionId: String, newName: String) {
        val currentSong = _uiState.value.song ?: return
        val sectionIndex = currentSong.sections.indexOfFirst { it.id == sectionId }
        if (sectionIndex == -1) return
        
        val section = currentSong.sections[sectionIndex]
        val newSections = currentSong.sections.toMutableList()
        newSections[sectionIndex] = section.copy(title = newName)
        updateSong(currentSong.copy(sections = newSections))
    }

    fun harmonyChangeBpm(bpm: Int) {
        val currentSong = _uiState.value.song ?: return
        updateSong(currentSong.copy(bpm = bpm), metadataChanged = true)
    }

    fun harmonyUpdateSongTitle(title: String) {
        val currentSong = _uiState.value.song ?: return
        updateSong(currentSong.copy(title = title), metadataChanged = true)
    }

    fun harmonyChangeCapo(capo: Int) {
        onCapoChange(capo)
    }

    fun reorderSections(sectionIds: List<String>) {
        val currentSong = _uiState.value.song ?: return
        val sectionsMap = currentSong.sections.associateBy { it.id }
        val reorderedSections = sectionIds.mapNotNull { sectionsMap[it] }
        
        // Safety check: Don't lose sections if AI missed some
        val missingIds = currentSong.sections.map { it.id }.toSet() - sectionIds.toSet()
        val missingSections = missingIds.mapNotNull { sectionsMap[it] }
        
        updateSong(currentSong.copy(sections = reorderedSections + missingSections))
    }

    fun harmonyGetRhymes(word: String) {
        harmonyOpenWordFinder(word)
    }

    fun setMetronomeEnabled(enabled: Boolean) {
        val currentSong = _uiState.value.song ?: return
        val studioState = currentSong.studioState ?: songwritersnotebook.app.data.StudioState()
        updateSong(currentSong.copy(studioState = studioState.copy(isMetronomeEnabled = enabled)))
    }

    fun transposeSong(semitones: Int, newChordDefinitions: List<songwritersnotebook.app.data.ChordDefinition> = emptyList()) {
        val currentSong = _uiState.value.song ?: return
        if (semitones == 0 && newChordDefinitions.isEmpty()) return

        // 1. Prepare New Library
        val aiDefsMap = newChordDefinitions.associateBy { it.name }
        
        val newLibrary = currentSong.chordDefinitions.mapValues { (oldName, chord) ->
            val transposedName = songwritersnotebook.app.utils.ChordTransposer.transposeChordName(oldName, semitones)
            
            // Use AI suggestion if available, otherwise programmatically shift
            aiDefsMap[transposedName]?.copy(name = transposedName) 
                ?: chord.copy(
                    name = transposedName,
                    guitarTab = songwritersnotebook.app.utils.ChordTransposer.transposeGuitarTab(chord.guitarTab, semitones)
                )
        }.mapKeys { (oldName, _) ->
            songwritersnotebook.app.utils.ChordTransposer.transposeChordName(oldName, semitones)
        }

        // 2. Transpose Lyrics (Chord Tags)
        val newSections = currentSong.sections.map { section ->
            section.copy(lines = section.lines.map { line ->
                val newText = songwritersnotebook.app.utils.ChordTransposer.transposeLyrics(line.text, semitones)
                line.copy(text = newText)
            })
        }

        updateSong(currentSong.copy(
            chordDefinitions = newLibrary,
            sections = newSections
        ))
    }

    fun renameRecording(recordingId: String, newLabel: String) {
        val currentSong = _uiState.value.song ?: return
        val updatedRecordings = currentSong.recordings.toMutableMap()
        
        var found = false
        for ((targetId, list) in updatedRecordings) {
            val idx = list.indexOfFirst { it.id == recordingId }
            if (idx != -1) {
                val newList = list.toMutableList()
                newList[idx] = newList[idx].copy(label = newLabel)
                updatedRecordings[targetId] = newList
                found = true
                break
            }
        }
        
        if (found) {
            updateSong(currentSong.copy(recordings = updatedRecordings))
        }
    }

    fun harmonyAddTab(
        sectionId: String,
        lineIndex: Int?,
        name: String,
        beats: Int,
        subdivisions: Int,
        columns: List<List<String>>,
        recordingId: String?
    ) {
        val currentSong = _uiState.value.song ?: return
        val sectionIndex = currentSong.sections.indexOfFirst { it.id == sectionId }
        if (sectionIndex == -1) return
        
        val section = currentSong.sections[sectionIndex]
        
        val tabColumns = columns.map { col ->
            val processedNotes = col.map { fretStr ->
                if (fretStr.lowercase() == "x") "x" else (fretStr.toIntOrNull() ?: 0).toString()
            }
            val safeNotes = MutableList(6) { "-" }
            for (i in 0 until minOf(6, processedNotes.size)) {
                safeNotes[i] = processedNotes[i]
            }
            songwritersnotebook.app.data.TabColumn(notes = safeNotes)
        }
        
        val newTab = songwritersnotebook.app.data.GuitarTab(
            id = com.benasher44.uuid.uuid4().toString(),
            name = name,
            beats = beats,
            subdivisions = subdivisions,
            columns = tabColumns,
            recordingIds = if (recordingId != null) listOf(recordingId) else emptyList()
        )
        
        if (lineIndex == null) {
            val newSections = currentSong.sections.toMutableList()
            newSections[sectionIndex] = section.copy(tabs = section.tabs + newTab)
            updateSong(currentSong.copy(sections = newSections))
        } else {
            if (lineIndex < 0 || lineIndex >= section.lines.size) return
            val newLine = section.lines[lineIndex].copy(tabs = section.lines[lineIndex].tabs + newTab)
            val newLines = section.lines.toMutableList()
            newLines[lineIndex] = newLine
            val newSections = currentSong.sections.toMutableList()
            newSections[sectionIndex] = section.copy(lines = newLines)
            updateSong(currentSong.copy(sections = newSections))
        }
    }

    fun harmonyUpdateGuitarTab(
        tabId: String,
        sectionId: String,
        lineIndex: Int?,
        name: String?,
        beats: Int?,
        subdivisions: Int?,
        columns: List<List<String>>?,
        recordingId: String?
    ) {
        val currentSong = _uiState.value.song ?: return
        val sectionIndex = currentSong.sections.indexOfFirst { it.id == sectionId }
        if (sectionIndex == -1) return
        
        val section = currentSong.sections[sectionIndex]
        
        fun updateTabList(list: List<songwritersnotebook.app.data.GuitarTab>): List<songwritersnotebook.app.data.GuitarTab> {
            val idx = list.indexOfFirst { it.id == tabId }
            if (idx == -1) return list
            val updated = list.toMutableList()
            var tab = list[idx]
            
            if (name != null) tab = tab.copy(name = name)
            if (beats != null) tab = tab.copy(beats = beats)
            if (subdivisions != null) tab = tab.copy(subdivisions = subdivisions)
            if (recordingId != null && !tab.recordingIds.contains(recordingId)) {
                tab = tab.copy(recordingIds = tab.recordingIds + recordingId)
            }
            if (columns != null) {
                tab = tab.copy(columns = columns.map { col ->
                    val processedNotes = col.map { fretStr ->
                        if (fretStr.lowercase() == "x") "x" else (fretStr.toIntOrNull() ?: 0).toString()
                    }
                    val safeNotes = MutableList(6) { "-" }
                    for (i in 0 until minOf(6, processedNotes.size)) {
                        safeNotes[i] = processedNotes[i]
                    }
                    songwritersnotebook.app.data.TabColumn(notes = safeNotes)
                })
            }
            
            updated[idx] = tab
            return updated
        }
        
        val newSections = currentSong.sections.toMutableList()
        if (lineIndex == null) {
            newSections[sectionIndex] = section.copy(tabs = updateTabList(section.tabs))
        } else {
            if (lineIndex < 0 || lineIndex >= section.lines.size) return
            val newLines = section.lines.toMutableList()
            newLines[lineIndex] = section.lines[lineIndex].copy(tabs = updateTabList(section.lines[lineIndex].tabs))
            newSections[sectionIndex] = section.copy(lines = newLines)
        }
        
        updateSong(currentSong.copy(sections = newSections))
    }

    fun harmonyUpdateChordInLibrary(chordName: String, newName: String?, newPianoNotes: List<String>?, newGuitarTab: List<String>?) {
        val currentSong = _uiState.value.song ?: return
        val existingChord = currentSong.chordDefinitions[chordName] ?: return
        
        val finalName = newName ?: chordName
        
        val finalGuitarTab = if (newGuitarTab != null) {
            val processed = newGuitarTab.map { fretStr ->
                if (fretStr.lowercase() == "x") "x" else (fretStr.toIntOrNull() ?: 0).toString()
            }
            if (processed.isEmpty()) null else processed
        } else {
             existingChord.guitarTab
        }
        
        val finalPianoNotes = if (newPianoNotes != null) {
             if (newPianoNotes.isEmpty()) null else newPianoNotes
        } else {
            existingChord.pianoNotes
        }
        
        val newDefinition = existingChord.copy(
            name = finalName,
            isCustom = true, // Force to custom since Harmony modified it
            root = null,
            quality = null,
            pianoNotes = finalPianoNotes,
            guitarTab = finalGuitarTab
        )
        
        val updatedDefinitions = currentSong.chordDefinitions.toMutableMap()
        
        if (newName != null && newName != chordName) {
            updatedDefinitions.remove(chordName)
            updatedDefinitions[newName] = newDefinition
            
            // Re-map inline chords inside lyric text
            val regex = """\[${Regex.escape(chordName)}\]""".toRegex()
            val updatedSections = currentSong.sections.map { section ->
                section.copy(lines = section.lines.map { line ->
                    line.copy(text = line.text.replace(regex, "[" + newName + "]"))
                })
            }
            updateSong(currentSong.copy(chordDefinitions = updatedDefinitions, sections = updatedSections))
        } else {
            updatedDefinitions[chordName] = newDefinition
            updateSong(currentSong.copy(chordDefinitions = updatedDefinitions))
        }
    }

    fun harmonyStartRecording(sectionId: String, lineIndex: Int?) {
        val currentSong = _uiState.value.song ?: return
        val sectionIndex = currentSong.sections.indexOfFirst { it.id == sectionId }
        if (sectionIndex == -1) return
        
        val section = currentSong.sections[sectionIndex]
        val targetId = if (lineIndex == null) {
            section.id
        } else {
            if (lineIndex < 0 || lineIndex >= section.lines.size) return
            section.lines[lineIndex].id
        }
        
        onStartRecordingFor(targetId)
    }

    fun harmonyOpenTabEditor(sectionId: String, lineIndex: Int?) {
        val currentSong = _uiState.value.song ?: return
        val section = currentSong.sections.find { it.id == sectionId } ?: return
        
        val targetId = if (lineIndex != null && lineIndex >= 0 && lineIndex < section.lines.size) {
            section.lines[lineIndex].id
        } else {
            section.id
        }
        
        openTabEditor(targetId)
    }

    fun harmonyNavigateToStudio() {
        val song = _uiState.value.song ?: return
        // This is a UI-level event, we should probably set a flag or call a callback
        // For now, if we have the lambda passed in, we'll need to trigger it
        // Or we can add a 'navigationRequest' to UI State
        _uiState.update { it.copy(showAiAssistantSheet = false) }
        // In Compose, the actual navigation happens via the onNavigateToStudio callback
        // We'll need to ensure the Screen is listening for an 'intent' to navigate
    }

    fun harmonyOpenWordFinder(searchTerm: String?) {
        _uiState.update { it.copy(showWordFinderPopup = true, wordFinderSearchTerm = searchTerm ?: it.wordFinderSearchTerm) }
        if (searchTerm != null) {
            findWords()
        }
    }

    fun harmonySetMetronomeMute(muted: Boolean) {
        // Metronome is only visible/active on recording sheet
        if (!_uiState.value.showRecordingSheet) return
        _uiState.update { it.copy(isMetronomeMuted = muted) }
        audioHandler.setMetronomeMuted(muted)
    }

    fun harmonySetMetronomeEnabled(enabled: Boolean) {
        if (!_uiState.value.showRecordingSheet) return
        if (_uiState.value.isMetronomeEnabled == enabled) return
        toggleMetronomeEnabled()
    }

    fun harmonyChangeSetting(settingName: String, value: Boolean) {
        val currentSettings = _uiState.value.editorSettings
        val updatedSettings = when (settingName.lowercase()) {
            "showtabs", "show_tabs" -> currentSettings.copy(showTabs = value)
            "showsectionheaders", "show_section_headers" -> currentSettings.copy(showSectionHeaders = value)
            "rhyming", "rhymingenabled", "rhyming_enabled" -> currentSettings.copy(rhymingEnabled = value)
            "syllablecount", "syllable_count" -> currentSettings.copy(syllableCountEnabled = value)
            "slantrhymes", "slant_rhymes" -> currentSettings.copy(showSlantRhymes = value)
            "endoflineonly", "end_of_line_only" -> currentSettings.copy(endOfLineOnly = value)
            "showpiano", "showpianoinfo", "show_piano_info" -> currentSettings.copy(showPianoInfo = value)
            "showguitar", "showguitarinfo", "show_guitar_info" -> currentSettings.copy(showGuitarInfo = value)
            else -> currentSettings
        }
        _uiState.update { it.copy(editorSettings = updatedSettings) }
        // Persist
        if (settingName.lowercase().contains("tabs")) preferencesManager.showTabs = value
    }

    fun harmonyMoveItem(itemId: String, direction: String) {
        selectItemById(itemId)
        if (direction.lowercase() == "up") moveSelectedItemUp() else moveSelectedItemDown()
    }

    fun harmonyDuplicateItem(itemId: String) {
        selectItemById(itemId)
        duplicateSelectedItem()
    }

    fun harmonyDeleteItem(itemId: String) {
        selectItemById(itemId)
        deleteSelectedItem()
    }

    fun harmonyDeleteChordFromLibrary(chordName: String) {
        onDeleteChordClicked(chordName)
        confirmChordDeletion()
    }

    fun harmonySplitLine(sectionId: String, lineIndex: Int, cursorPosition: Int) {
        val currentSong = _uiState.value.song ?: return
        val sectionIdx = currentSong.sections.indexOfFirst { it.id == sectionId }
        if (sectionIdx == -1) return
        val section = currentSong.sections[sectionIdx]
        val line = section.lines.getOrNull(lineIndex) ?: return
        
        val text = line.text
        val safePos = cursorPosition.coerceIn(0, text.length)
        val textForCurrent = text.substring(0, safePos)
        val textForNew = text.substring(safePos)
        
        splitLine(sectionIdx, lineIndex, TextFieldValue(text, selection = TextRange(safePos)))
    }

    fun harmonyMergeLineWithPrevious(sectionId: String, lineIndex: Int) {
        val currentSong = _uiState.value.song ?: return
        val sectionIdx = currentSong.sections.indexOfFirst { it.id == sectionId }
        if (sectionIdx == -1) return
        val text = currentSong.sections[sectionIdx].lines.getOrNull(lineIndex)?.text ?: ""
        mergeLineWithPrevious(sectionIdx, lineIndex, text)
    }

    fun harmonyMergeSectionWithPrevious(sectionId: String) {
        val currentSong = _uiState.value.song ?: return
        val sectionIdx = currentSong.sections.indexOfFirst { it.id == sectionId }
        if (sectionIdx == -1) return
        mergeSectionWithPrevious(sectionIdx)
    }

    fun harmonySetChordDisplayMode(mode: String) {
        val displayMode = when (mode.lowercase()) {
            "large" -> songwritersnotebook.app.viewmodels.ChordDisplayMode.Large
            "small" -> songwritersnotebook.app.viewmodels.ChordDisplayMode.Small
            "off" -> songwritersnotebook.app.viewmodels.ChordDisplayMode.Off
            else -> return
        }
        setChordDisplayMode(displayMode)
    }

    fun harmonyToggleShowNotes(show: Boolean?) {
        val current = _uiState.value.song?.showNotes ?: false
        val target = show ?: !current
        if (target != current) toggleShowNotes()
    }

    fun harmonyRenameRecording(recordingId: String, newName: String) {
        val song = _uiState.value.song ?: return
        val recording = song.recordings.values.flatten().find { it.id == recordingId } ?: return
        
        // We need to find which targetId it belongs to
        var targetId: String? = null
        for ((tid, list) in song.recordings) {
            if (list.any { it.id == recordingId }) {
                targetId = tid
                break
            }
        }
        
        if (targetId != null) {
            val currentTargetId = _uiState.value.editingTabTargetId
            _uiState.update { it.copy(editingTabTargetId = targetId) }
            renameRecording(recording, newName)
            _uiState.update { it.copy(editingTabTargetId = currentTargetId) }
        }
    }

    fun harmonyDeleteRecording(recordingId: String) {
        val song = _uiState.value.song ?: return
        val recording = song.recordings.values.flatten().find { it.id == recordingId } ?: return
        
        var targetId: String? = null
        for ((tid, list) in song.recordings) {
            if (list.any { it.id == recordingId }) {
                targetId = tid
                break
            }
        }
        
        if (targetId != null) {
            deleteRecording(targetId, recording)
        }
    }

    fun harmonyPlayRecording(recordingId: String) {
        val song = _uiState.value.song ?: return
        val recording = song.recordings.values.flatten().find { it.id == recordingId } ?: return
        playRecording(recording)
    }

    fun harmonyOpenMediaForLine(sectionId: String, lineIndex: Int?) {
        val song = _uiState.value.song ?: return
        val section = song.sections.find { it.id == sectionId } ?: return
        val targetId = if (lineIndex != null) {
            section.lines.getOrNull(lineIndex)?.id ?: return
        } else {
            section.id
        }
        openLineMediaSheet(targetId)
    }

    private fun selectItemById(itemId: String) {
        val song = _uiState.value.song ?: return
        song.sections.forEachIndexed { sIdx, section ->
            if (section.id == itemId) {
                selectItem(sIdx, null)
                return
            }
            section.lines.forEachIndexed { lIdx, line ->
                if (line.id == itemId) {
                    selectItem(sIdx, lIdx)
                    return
                }
            }
        }
    }

    // --- Recording Sheet ---
    fun saveRecording(filePath: String, customName: String? = null) {
        if (isViewer) {
            onDismissRecordingSheet()
            return
        }
        val currentSong = _uiState.value.song ?: return
        val target = _uiState.value.recordingTarget ?: return
        
        val targetId = target.id
        val idToReplace = target.idToReplace
        val songId = currentSong.id
        val label = customName ?: target.name
        
        hasUnsavedChanges.value = true
        
        //Consolidate everything into one sequential block to ensure the file exists before upload
        viewModelScope.launch(songwritersnotebook.app.AppDispatchers.IO) {
            println("DEBUG_VM: saveRecording sequence started. tempPath=$filePath")
            val tempRecordingId = com.benasher44.uuid.uuid4().toString()
            val permanentPath = songRepository?.getLocalFileForRecording(tempRecordingId) ?: filePath
            println("DEBUG_VM: Target permanentPath=$permanentPath")
            
            try {
                val localFile = fileSystem.getFile(filePath)
                
                // Wait up to 2 seconds for the file to actually exist (handles slow closure by recorder)
                var retries = 0
                while (!localFile.exists() && retries < 10) {
                    println("DEBUG_VM: Waiting for file to appear... retry $retries")
                    kotlinx.coroutines.delay(200)
                    retries++
                }

                if (localFile.exists()) {
                    println("DEBUG_VM: Source file found. Size: ${localFile.length()} bytes")
                    if (permanentPath != filePath) {
                        val permanentFile = fileSystem.getFile(permanentPath)
                        // Ensure parent directory exists
                        val parentDir = permanentFile.absolutePath.substringBeforeLast("/")
                        fileSystem.getFile(parentDir).mkdirs()
                        println("DEBUG_VM: Parent directory ensured: $parentDir")
                        
                        // Small additional delay to ensure file system is ready for the move
                        kotlinx.coroutines.delay(200)
                        
                        localFile.copyTo(permanentFile, overwrite = true)
                        println("DEBUG_VM: Copy successful. Checking permanent file existence...")
                        if (permanentFile.exists()) {
                            println("DEBUG_VM: Permanent file exists. Size: ${permanentFile.length()} bytes. Deleting temp...")
                            localFile.delete()
                            println("DEBUG_VM: Temp file deleted successfully.")
                        } else {
                            println("DEBUG_VM: ERROR: Permanent file does NOT exist after copy attempt!")
                        }
                    } else {
                        println("DEBUG_VM: Permanent path is same as temp path. No move needed.")
                    }
                } else {
                    println("DEBUG_VM: CRITICAL ERROR: Recording file never appeared at $filePath after retries")
                    // List files in temp dir to see what's actually there
                    try {
                        val tempDir = fileSystem.getFile(filePath.substringBeforeLast("/"))
                        val files = tempDir.listFiles()
                        println("DEBUG_VM: Files in temp dir (${tempDir.absolutePath}):")
                        files?.forEach { println("  - ${it.absolutePath} (${it.length()} bytes)") }
                    } catch (e: Exception) {
                        println("DEBUG_VM: Could not list temp files: $e")
                    }
                }
            } catch (e: Exception) {
                println("DEBUG_VM: Error moving recording to permanent path: $e")
                e.printStackTrace()
            }

            // Get ACTUAL duration from file before saving metadata
            val waveformResult = try {
                WaveformGenerator.generateWaveform(permanentPath)
            } catch (e: Exception) {
                println("DEBUG_VM: Error generating duration for new recording: $e")
                null
            }

            val tempMetadata = songwritersnotebook.app.data.RecordingMetadata(
                id = tempRecordingId,
                filePath = permanentPath,
                label = label,
                timestamp = Clock.System.now().toEpochMilliseconds(),
                durationMs = waveformResult?.durationMillis ?: 0L,
                isSynced = false
            )
            
            val currentRecordings = currentSong.recordings.toMutableMap()
            
            // Add to primary target
            val listForTarget = currentRecordings[targetId] ?: emptyList()
            val updatedList = if (idToReplace != null) {
                listForTarget.filter { it.id != idToReplace } + tempMetadata
            } else {
                listForTarget + tempMetadata
            }
            currentRecordings[targetId] = updatedList
            
            // Add to parent target if it exists (e.g. tab recording added to line list)
            if (target.parentTargetId != null && target.parentTargetId != targetId) {
                val parentList = currentRecordings[target.parentTargetId] ?: emptyList()
                val updatedParentList = if (idToReplace != null) {
                    parentList.filter { it.id != idToReplace } + tempMetadata
                } else {
                    parentList + tempMetadata
                }
                currentRecordings[target.parentTargetId] = updatedParentList
            }
            
            var updatedSections = currentSong.sections
            if (target.associatedTabId != null) {
                val tabId = target.associatedTabId
                updatedSections = updatedSections.map { section ->
                    section.copy(
                        tabs = section.tabs.map { if (it.id == tabId) it.copy(recordingIds = listOf(tempMetadata.id)) else it },
                        lines = section.lines.map { line ->
                            line.copy(tabs = line.tabs.map { if (it.id == tabId) it.copy(recordingIds = listOf(tempMetadata.id)) else it })
                        }
                    )
                }
                
                // CRITICAL: Update editingTab state immediately so the change is reflected in the open TabEditorSheet
                _uiState.update { state ->
                    if (state.editingTab?.id == tabId) {
                        state.copy(editingTab = state.editingTab.copy(recordingIds = listOf(tempMetadata.id)))
                    } else {
                        state
                    }
                }
            }
            
            var updatedStudioState = currentSong.studioState
            if (idToReplace != null && updatedStudioState != null) {
                 updatedStudioState = updatedStudioState.copy(
                        tracks = updatedStudioState.tracks.map { track ->
                            track.copy(
                                items = track.items.map { trackItem ->
                                    if (trackItem.data.id == idToReplace) {
                                        trackItem.copy(data = trackItem.data.copy(id = tempMetadata.id, url = tempMetadata.filePath, name = tempMetadata.label))
                                    } else {
                                        trackItem
                                    }
                                }
                            )
                        }
                    )
            }
            
            val updatedSong = currentSong.copy(
                recordings = currentRecordings,
                sections = updatedSections,
                studioState = updatedStudioState,
                lastModified = Clock.System.now()
            )
            
            println("DEBUG_VM: Metadata updated locally. Updating UI and triggering sync...")
            withContext(kotlinx.coroutines.Dispatchers.Main) {
                updateSong(updatedSong)
            }
            
            // Background Upload using the permanent path - safely triggered AFTER move
            println("DEBUG_VM: Triggering cloud upload for $permanentPath")
            songRepository?.uploadRecordingNonBlocking(
                 path = permanentPath,
                 songId = songId,
                 targetId = targetId,
                 tempId = tempRecordingId,
                 label = label,
                 replaceId = idToReplace
            )
        }
        
        onDismissRecordingSheet()
    }
    
    fun linkRecordingToTab(tabId: String, recording: songwritersnotebook.app.data.RecordingMetadata) {
        val currentSong = _uiState.value.song ?: return

        val updatedSections = currentSong.sections.map { section ->
            section.copy(
                tabs = section.tabs.map { if (it.id == tabId) it.copy(recordingIds = listOf(recording.id)) else it },
                lines = section.lines.map { line ->
                    line.copy(tabs = line.tabs.map { if (it.id == tabId) it.copy(recordingIds = listOf(recording.id)) else it })
                }
            )
        }

        val updatedSong = currentSong.copy(sections = updatedSections, lastModified = Clock.System.now())

        // CRITICAL: Update editingTab state immediately
        _uiState.update { state ->
            if (state.editingTab?.id == tabId) {
                state.copy(editingTab = state.editingTab.copy(recordingIds = listOf(recording.id)))
            } else {
                state
            }
        }

        updateSong(updatedSong)
    }
    fun onDismissRecordingSheet() {
        audioHandler.stopPlayback()
        audioHandler.stopRecordingNonBlocking()
        val target = _uiState.value.recordingTarget
        if (target != null) {
            try {
                val tempFile = fileSystem.getFile(target.outputFile)
                if (tempFile.exists()) {
                    tempFile.delete()
                    println("DEBUG_VM: Deleted temporary recording file: ${target.outputFile}")
                }
            } catch (e: Exception) {
                println("DEBUG_VM: Failed to delete temp recording file: ${e.message}")
            }
        }
        _uiState.update { it.copy(showRecordingSheet = false, recordingTarget = null) }
    }
    
    // --- Media Deletion ---
    fun deleteRecording(targetId: String, recording: songwritersnotebook.app.data.RecordingMetadata) {
        if (isViewer) return
        val currentSong = _uiState.value.song ?: return
        
        // Optimistic UI update: Remove from recordings map
        val currentList = currentSong.recordings[targetId] ?: return
        val updatedRecordings = currentSong.recordings + (targetId to currentList.filter { it.id != recording.id })
        
        // Also remove from any tabs that reference it
        val updatedSections = currentSong.sections.map { section ->
            section.copy(
                tabs = section.tabs.map { 
                    if (it.recordingIds.contains(recording.id)) it.copy(recordingIds = it.recordingIds - recording.id) else it 
                },
                lines = section.lines.map { line ->
                    line.copy(tabs = line.tabs.map { 
                        if (it.recordingIds.contains(recording.id)) it.copy(recordingIds = it.recordingIds - recording.id) else it 
                    })
                }
            )
        }
        
        val updatedSong = currentSong.copy(recordings = updatedRecordings, sections = updatedSections)
        
        // Update editingTab if it was referencing the deleted recording
        val currentEditingTab = _uiState.value.editingTab
        val newEditingTab = if (currentEditingTab != null && currentEditingTab.recordingIds.contains(recording.id)) {
            currentEditingTab.copy(recordingIds = currentEditingTab.recordingIds - recording.id)
        } else {
            currentEditingTab
        }
        
        _uiState.update { it.copy(song = updatedSong, editingTab = newEditingTab) }
        findRhymes(updatedSong, _uiState.value.editorSettings)
        hasUnsavedChanges.value = true

        
        // Background deletion
        viewModelScope.launch(songwritersnotebook.app.AppDispatchers.IO) {
            songRepository?.deleteRecordingFromFirebase(currentSong.id, recording.id)
        }
    }
    
    fun unlinkRecordingFromTab(tabId: String) {
        val currentSong = _uiState.value.song ?: return

        val updatedSections = currentSong.sections.map { section ->
            section.copy(
                tabs = section.tabs.map { if (it.id == tabId) it.copy(recordingIds = emptyList()) else it },
                lines = section.lines.map { line ->
                    line.copy(tabs = line.tabs.map { if (it.id == tabId) it.copy(recordingIds = emptyList()) else it })
                }
            )
        }

        val updatedSong = currentSong.copy(sections = updatedSections, lastModified = Clock.System.now())
        
        // CRITICAL: Update editingTab state immediately
        _uiState.update { state ->
            if (state.editingTab?.id == tabId) {
                state.copy(editingTab = state.editingTab.copy(recordingIds = emptyList()))
            } else {
                state
            }
        }
        
        updateSong(updatedSong)
    }
    
    fun retryUpload(recording: songwritersnotebook.app.data.RecordingMetadata) {
        val currentSong = _uiState.value.song ?: return
        
        // Find targetId
        var targetId: String? = null
        for ((tid, list) in currentSong.recordings) {
            if (list.any { it.id == recording.id }) {
                targetId = tid
                break
            }
        }
        
        if (targetId != null) {
            val path = recording.filePath
            // Only upload if it looks like a local file
            if (!path.startsWith("http")) {
                songRepository?.uploadRecordingNonBlocking(
                    path = path,
                    songId = currentSong.id,
                    targetId = targetId,
                    tempId = recording.id,
                    label = recording.label,
                    replaceId = recording.id // Replace self to update URL/isSynced
                )
            }
        }
    }

    fun toggleMetronomeEnabled() {
        val newState = !_uiState.value.isMetronomeEnabled
        _uiState.update { it.copy(isMetronomeEnabled = newState) }
        if (newState) {
            val bpm = _uiState.value.song?.bpm ?: 120
            audioHandler.startMetronome(bpm) { beat ->
                viewModelScope.launch { _uiState.update { it.copy(currentMetronomeBeat = beat) } }
            }
            audioHandler.setMetronomeMuted(_uiState.value.isMetronomeMuted)
        } else {
            audioHandler.stopMetronome()
            _uiState.update { it.copy(currentMetronomeBeat = -1) }
        }
    }

    fun toggleMetronomePreference() {
        val newState = !_uiState.value.isMetronomeEnabled
        _uiState.update { it.copy(isMetronomeEnabled = newState) }
        if (!newState) {
            audioHandler.stopMetronome()
            _uiState.update { it.copy(currentMetronomeBeat = -1) }
        }
    }

    fun toggleMetronomeMuted() {
        val newMute = !_uiState.value.isMetronomeMuted
        _uiState.update { it.copy(isMetronomeMuted = newMute) }
        audioHandler.setMetronomeMuted(newMute)
    }

    fun updateBPM(bpm: Int) {
        if (isViewer) return
        val currentSong = _uiState.value.song ?: return
        updateSong(currentSong.copy(bpm = bpm), metadataChanged = true)
        if (_uiState.value.isMetronomeEnabled) {
            audioHandler.startMetronome(bpm) { beat ->
                viewModelScope.launch { _uiState.update { it.copy(currentMetronomeBeat = beat) } }
            }
        }
    }

    fun onBpmChange(bpmString: String) {
        if (isViewer) return
        val bpm = bpmString.trim().toIntOrNull() ?: return
        if (bpm < 1 || bpm > 999) return // Range validation
        
        // 1. Immediate Visual Update (Local State)
        _uiState.update { state ->
            state.copy(song = state.song?.copy(bpm = bpm))
        }

        // 2. Immediate Audio Update
        if (_uiState.value.isMetronomeEnabled) {
            audioHandler.startMetronome(bpm) { beat ->
                viewModelScope.launch { _uiState.update { it.copy(currentMetronomeBeat = beat) } }
            }
        }
    }

    fun onBpmChangeRelease(bpmString: String) {
        if (isViewer) return
        val bpm = bpmString.trim().toIntOrNull() ?: return
        if (bpm < 1 || bpm > 999) return // Range validation
        
        // Cancel any previous debounce just in case
        bpmDebounceJob?.cancel()
        
        // Immediately sync to CRDT
        val currentSong = _uiState.value.song ?: return
        updateSong(currentSong.copy(bpm = bpm), metadataChanged = true)
    }
    
    // --- Misc ---
    fun openAddMediaDialog() { _uiState.update { it.copy(showAddMediaDialog = true) } }
    fun dismissAddMediaDialog() { _uiState.update { it.copy(showAddMediaDialog = false) } }
    
    fun clearPresenceOnExit() {
        val songId = _uiState.value.song?.id ?: return
        songRepository?.repositoryScope?.launch {
            try {
                songRepository?.clearPresence(songId)
            } catch (e: Exception) {
                println("DEBUG_REPO: Error clearing presence on exit: $e")
            }
        }
    }

    override fun onCleared() {
        super.onCleared()
        // 1. Hard cancel all background jobs to prevent ghost saves/leaks
        debounceSaveJob?.cancel()
        presenceHeartbeatJob?.cancel()

        audioHandler.release()

        // 2. Clear Presence Instantly
        clearPresenceOnExit()

        // Final Sync on exit - Perform a hard save if there are unsaved changes
        val song = _uiState.value.song
        if (song != null && !isViewer && song.id.isNotEmpty()) {
            if (hasSessionEdits) {
                // INSTANT FINAL PUSH: Save immediately using the survivor scope
                songRepository?.finalSaveAndStop(
                    song = song,
                    scope = songwritersnotebook.app.crdt.UndoScope.EDITOR,
                    userId = currentUserId ?: "anonymous",
                    observerId = observerId,
                    deletedLineIds = deletedLineIds.toList(),
                    deletedSectionIds = deletedSectionIds.toList(),
                    deletedTrackIds = deletedTrackIds.toList(),
                    deletedTrackItemIds = deletedTrackItemIds.toList(),
                    sessionVersionId = currentSessionVersionId
                )
            } else {
                songRepository?.stopSongObservation(song.id, currentUserId ?: "anonymous", observerId)
            }
        } else {
            songRepository?.stopSongObservation(null, currentUserId ?: "anonymous", observerId)
        }
    }

    fun toggleFavoriteChord(chord: ChordDefinition) {
        val state = _uiState.value
        val tuning = state.guitarTuning
        viewModelScope.launch {
            chordLibraryRepository?.toggleFavorite(chord, tuning, false)
            checkIfChordIsFavorited()
        }
    }

    // --- Version History ---
    fun onVersionHistoryClicked() {
        // 1. Check Tier
        if (currentSubscriptionTier == SubscriptionTier.FREE) {
            _uiState.update { it.copy(showUpgradeDialog = true, upgradeMessage = "Version History is a premium feature. Upgrade to unlock it!") }
            return
        }
        
        // 2. Check Permissions (Owner Only)
        if (isShared) {
            _uiState.update { it.copy(showVersionHistoryOwnerInfoDialog = true) }
            return
        }
        
        // 3. Allow
        toggleVersionHistorySheet()
    }

    fun dismissUpgradeDialog() {
        _uiState.update { it.copy(showUpgradeDialog = false) }
    }

    fun dismissVersionHistoryOwnerInfoDialog() {
        _uiState.update { it.copy(showVersionHistoryOwnerInfoDialog = false) }
    }

    fun toggleVersionHistorySheet() {
        if (currentSubscriptionTier == SubscriptionTier.FREE) {
            _uiState.update { it.copy(showUpgradeDialog = true, upgradeMessage = "Version History is a premium feature. Upgrade to unlock it!") }
            return
        }
        _uiState.update { it.copy(showVersionHistorySheet = !it.showVersionHistorySheet) }
    }

    fun previewVersion(version: SongVersion) {
        _uiState.update { it.copy(versionPreview = version.songData, showVersionHistorySheet = false) }
    }

    fun exitPreview() {
        _uiState.update { it.copy(versionPreview = null, showVersionHistorySheet = true) }
    }

    fun commitRestore() {
        val preview = _uiState.value.versionPreview ?: return
        
        val currentSong = _uiState.value.song ?: return
        val restoredData = preview.copy(
            id = currentSong.id,
            ownerId = currentSong.ownerId,
            collaborators = currentSong.collaborators,
            viewers = currentSong.viewers,
            lastModified = Clock.System.now()
        )
        
        _uiState.update { it.copy(versionPreview = null) }
        updateSong(restoredData)
        performSave()
    }

    fun deleteVersion(version: SongVersion) {
        val songId = _uiState.value.song?.id ?: return
        viewModelScope.launch {
            songRepository?.deleteVersion(songId, version.id)
        }
    }

    fun saveManualVersion(notes: String) {
        val song = _uiState.value.song ?: return
        if (isViewer || isShared) return 
        
        viewModelScope.launch {
            songRepository?.saveVersion(song, VersionType.MANUAL, notes)
        }
    }

    fun restoreVersion(version: SongVersion) {
        if (isViewer || isShared) {
            // If in preview, allow restore if it's the owner
            if (_uiState.value.versionPreview != null && !isShared) {
                // proceed
            } else {
                return
            }
        }
        val currentSong = _uiState.value.song ?: return
        val restoredData = version.songData.copy(
            id = currentSong.id, 
            ownerId = currentSong.ownerId,
            collaborators = currentSong.collaborators,
            viewers = currentSong.viewers,
            lastModified = Clock.System.now()
        )
        
        _uiState.update { it.copy(versionPreview = null) }
        updateSong(restoredData)
        performSave()
        toggleVersionHistorySheet()
    }
    
    fun updateVersionNotes(version: SongVersion, notes: String) {
        val songId = _uiState.value.song?.id ?: return
        viewModelScope.launch {
            songRepository?.updateVersionNotes(songId, version.id, notes)
        }
    }

    private fun checkIfChordIsFavorited() {
        val state = _uiState.value
        val favorites = chordLibraryRepository?.favoriteChords?.value ?: emptyList()
        
        val currentPianoNotes = state.manualKeys.sorted()
        val currentGuitarTab = state.currentTab.map { it.toString() }
        val currentTuning = state.guitarTuning

        val isFavorited = favorites.any { fav ->
            val tuningMatches = fav.tuning == currentTuning
            val tabMatches = fav.chord.guitarTab == currentGuitarTab
            val notesMatches = fav.chord.pianoNotes?.sorted() == currentPianoNotes
            
            tuningMatches && tabMatches && notesMatches
        }
        _uiState.update { it.copy(isFavorited = isFavorited) }
    }
    
    fun playFavoriteChord(savedChord: songwritersnotebook.app.data.SavedChord) {
        val state = _uiState.value
        val instrument = state.selectedInstrument
        val chordDef = savedChord.chord
        
        val notesToPlay = if (instrument == "guitar") {
            chordDef.guitarTab?.map { it.toIntOrNull() ?: if (it == "x") 'x' else 0 }?.let {
                songwritersnotebook.app.utils.ChordUtils.tabToNotes(it, savedChord.tuning, 0)
            } ?: emptyList()
        } else chordDef.pianoNotes ?: emptyList()

        if (notesToPlay.isNotEmpty()) {
            val safeOffset = if (state.strumOffset.isNaN()) 40f else state.strumOffset
            val strumDelayMs = (safeOffset / 120f * 100).toLong().coerceAtLeast(10)
            viewModelScope.launch {
                notesToPlay.forEachIndexed { index, note ->
                    audioHandler.playNote(note, instrument)
                    if (index < notesToPlay.size - 1 && strumDelayMs > 0) kotlinx.coroutines.delay(strumDelayMs)
                }
            }
        }
    }

    fun selectFavorite(savedChord: songwritersnotebook.app.data.SavedChord?) {
        _uiState.update { 
            val newSelected = if (it.selectedFavoriteChord?.id == savedChord?.id) null else savedChord
            it.copy(selectedFavoriteChord = newSelected) 
        }
    }
    
    fun addFavoriteToSong(savedChord: songwritersnotebook.app.data.SavedChord) {
        val currentSong = _uiState.value.song ?: return
        val newDefs = currentSong.chordDefinitions.toMutableMap()
        newDefs[savedChord.chord.name] = savedChord.chord
        updateSong(currentSong.copy(chordDefinitions = newDefs))
    }
    
    fun removeFavorite(savedChord: songwritersnotebook.app.data.SavedChord) {
        viewModelScope.launch {
            chordLibraryRepository?.toggleFavorite(savedChord.chord, savedChord.tuning, false) 
            if (_uiState.value.selectedFavoriteChord?.id == savedChord.id) {
                selectFavorite(null)
            }
            checkIfChordIsFavorited()
        }
    }
    
    fun onEditFavoriteClicked(savedChord: songwritersnotebook.app.data.SavedChord) {
        val def = savedChord.chord
        
        _uiState.update { 
            it.copy(
                isCustom = def.isCustom,
                selectedChordRoot = def.root ?: "",
                selectedChordQuality = def.quality ?: "",
                manualKeys = def.pianoNotes ?: emptyList(),
                currentTab = def.guitarTab?.map { t -> t.toIntOrNull() ?: if (t == "x") 'x' else t } ?: List(6) { 0 },
                guitarTuning = savedChord.tuning,
                guitarCapo = 0,
                
                showChordCreatorDialog = true,
                chordToEdit = null,
                selectedQualityCategoryIndex = if (def.quality != null) {
                    songwritersnotebook.app.utils.ChordUtils.CHORD_QUALITY_CATEGORIES.indexOfFirst { it.qualities.contains(def.quality) }.coerceAtLeast(0)
                } else 0
            )
        }
        checkIfChordIsFavorited()
    }

    private fun parseChordsFromLine(text: String): List<ChordInLine> {
        return chordRegex.findAll(text).mapIndexed {
            index, matchResult ->
            ChordInLine(name = matchResult.groupValues[1], index = index, range = matchResult.range)
        }.toList()
    }

    private fun getValidInsertionPoints(text: String): List<Int> {
        val invalidRanges = chordRegex.findAll(text).map { it.range }.toList()
        return (0..text.length).filter { position ->
            invalidRanges.none { range -> position > range.first && position <= range.last }
        }
    }
    
    fun onHarmonyActionApprove(actionId: String) {
        val action = _uiState.value.harmonyPendingActions.find { it.id == actionId } ?: return
        harmonyActionExecutor.execute(action.toolName, action.args)
        _uiState.update { it.copy(harmonyPendingActions = _uiState.value.harmonyPendingActions.filter { a -> a.id != actionId }) }
    }

    fun onHarmonyActionReject(actionId: String) {
        _uiState.update { it.copy(harmonyPendingActions = _uiState.value.harmonyPendingActions.filter { a -> a.id != actionId }) }
    }

    fun onHarmonyActionApproveAll() {
        _uiState.value.harmonyPendingActions.forEach { action ->
            harmonyActionExecutor.execute(action.toolName, action.args)
        }
        _uiState.update { it.copy(harmonyPendingActions = emptyList()) }
    }

    fun toggleHarmonyConfirmation() {
        val newValue = !_uiState.value.editorSettings.harmonyConfirmationRequired
        _uiState.update { it.copy(editorSettings = it.editorSettings.copy(harmonyConfirmationRequired = newValue)) }
        preferencesManager.harmonyConfirmationRequired = newValue
    }

    private fun findAndReselectItem(itemId: String) {
        val currentSong = _uiState.value.song ?: return
        currentSong.sections.forEachIndexed { sectionIdx, section ->
            if (section.id == itemId) {
                _uiState.update { it.copy(selectedItem = ItemSelection(itemId, sectionIdx, null)) }
                return
            }
            section.lines.forEachIndexed { lineIdx, line ->
                if (line.id == itemId) {
                    _uiState.update {
                        it.copy(selectedItem = ItemSelection(itemId, sectionIdx, lineIdx))
                    }
                    return
                }
            }
        }
    }

    private fun findRhymes(song: Song?, settings: EditorSettings, immediate: Boolean = false) {
        if (song == null) return
        
        if (!settings.rhymingEnabled) {
            _uiState.update { it.copy(rhymeData = emptyMap()) }
            return
        }

        rhymeJob?.cancel()
        rhymeJob = viewModelScope.launch {
            if (!immediate) kotlinx.coroutines.delay(600)
            
            var currentDb = database
            while (currentDb == null && isActive) {
                kotlinx.coroutines.delay(100)
                currentDb = database
            }
            if (currentDb == null) return@launch
            
            songwritersnotebook.app.data.RhymeRepository.initialize(currentDb)
            
            if (!songwritersnotebook.app.data.RhymeRepository.isInitialized.value) {
                songwritersnotebook.app.data.RhymeRepository.isInitialized.first { it }
            }

            val rhymeResult = songwritersnotebook.app.utils.RhymeService.findRhymesInSong(currentDb, song, settings)
            _uiState.update { it.copy(rhymeData = rhymeResult) }
        }
    }

    fun openMoveRecordingDialog(recording: RecordingMetadata) {
        _uiState.update { it.copy(showMoveRecordingDialog = true, recordingToMove = recording) }
    }

    fun dismissMoveRecordingDialog() {
        _uiState.update { it.copy(showMoveRecordingDialog = false, recordingToMove = null) }
    }

    fun moveRecording(toTargetId: String) {
        val recording = _uiState.value.recordingToMove ?: return
        val fromTargetId = _uiState.value.editingTabTargetId ?: return
        val currentSong = _uiState.value.song ?: return
        
        val currentRecordings = currentSong.recordings.toMutableMap()
        
        val sourceList = currentRecordings[fromTargetId] ?: emptyList()
        val newSourceList = sourceList.filter { it.id != recording.id }
        
        if (newSourceList.isEmpty()) {
            currentRecordings.remove(fromTargetId)
        } else {
            currentRecordings[fromTargetId] = newSourceList
        }
        
        val targetList = currentRecordings[toTargetId] ?: emptyList()
        if (targetList.none { it.id == recording.id }) {
            currentRecordings[toTargetId] = targetList + recording
        }
        
        val updatedSong = currentSong.copy(recordings = currentRecordings)
        updateSong(updatedSong)
        
        dismissMoveRecordingDialog()
    }

    fun onPresenceClicked(presence: songwritersnotebook.app.data.UserPresence) {
        if (_uiState.value.isShared) return
        _uiState.update { it.copy(selectedPresence = presence) }
    }

    fun onDismissPresenceCard() {
        _uiState.update { it.copy(selectedPresence = null) }
    }

    fun onCollaboratorAction(action: CollaboratorAction) {
        _uiState.update { it.copy(showCollaboratorActionDialog = true, collaboratorActionType = action) }
    }

    fun onToggleRoleClicked() {
        val presence = _uiState.value.selectedPresence ?: return
        val songId = _uiState.value.song?.id ?: return
        
        viewModelScope.launch {
            try {
                songRepository?.toggleCollaboratorRole(songId, presence.userId)
                _uiState.update { it.copy(selectedPresence = null) }
            } catch (e: Exception) {
                println("Error toggling role: $e")
            }
        }
    }

    fun dismissCollaboratorAction() {
        _uiState.update { it.copy(showCollaboratorActionDialog = false, collaboratorActionType = null) }
    }

    fun confirmCollaboratorAction() {
        val presence = _uiState.value.selectedPresence ?: return
        val action = _uiState.value.collaboratorActionType ?: return
        val songId = _uiState.value.song?.id ?: return

        viewModelScope.launch {
            try {
                when (action) {
                    CollaboratorAction.REMOVE -> {
                        songRepository?.removeCollaborator(songId, presence.userId)
                    }
                    CollaboratorAction.BLOCK -> {
                        songRepository?.removeCollaborator(songId, presence.userId)
                        songRepository?.blockUser(presence.userId, presence.email)
                    }
                }
                _uiState.update { it.copy(selectedPresence = null) }
            } catch (e: Exception) {
                println("Error performing collaborator action: $e")
            } finally {
                dismissCollaboratorAction()
            }
        }
    }

    // --- Progression Management ---

    fun onEditProgressionClicked(id: String) {
        _uiState.update { it.copy(showProgressionBuilderDialog = true, progressionToEditId = id) }
    }

    fun onDeleteProgressionClicked(id: String) {
        val song = _uiState.value.song ?: return
        val updatedProgressions = song.progressions.filterNot { it.id == id }
        val updatedSong = song.copy(progressions = updatedProgressions)
        updateSong(updatedSong, forceUndoStep = true)
        
        if (_uiState.value.activeProgressionId == id) {
            _uiState.update { it.copy(activeProgressionId = null, activeProgressionIndex = 0, selectedChordToPlace = null) }
        }
    }

    // --- Favorite Progressions ---
    
    fun toggleFavoriteProgression(progression: songwritersnotebook.app.data.ChordProgression) {
        viewModelScope.launch {
            chordLibraryRepository?.toggleFavoriteProgression(progression)
            
            // If the progression is in the current song, update its isFavorite status
            val song = _uiState.value.song ?: return@launch
            val existingProg = song.progressions.find { it.id == progression.id }
            if (existingProg != null) {
                val updatedProg = existingProg.copy(isFavorite = !existingProg.isFavorite)
                val updatedProgressions = song.progressions.map { if (it.id == updatedProg.id) updatedProg else it }
                val updatedSong = song.copy(progressions = updatedProgressions)
                updateSong(updatedSong, forceUndoStep = false)
            }
        }
    }

    fun selectFavoriteProgression(savedProgression: songwritersnotebook.app.data.SavedProgression?) {
        _uiState.update { it.copy(selectedFavoriteProgression = savedProgression) }
    }

    fun addFavoriteProgressionToSong(savedProgression: songwritersnotebook.app.data.SavedProgression) {
        val song = _uiState.value.song ?: return
        
        // Ensure all chords in the progression exist in the song's library
        var updatedSong = song
        savedProgression.progression.chordNames.forEach { chordName ->
            if (!updatedSong.chordDefinitions.containsKey(chordName)) {
                val newDef = songwritersnotebook.app.data.ChordDefinition(name = chordName)
                updatedSong = updatedSong.copy(
                    chordDefinitions = updatedSong.chordDefinitions + (chordName to newDef)
                )
            }
        }
        
        val newProg = savedProgression.progression.copy(
            id = com.benasher44.uuid.uuid4().toString(),
            isFavorite = true
        )
        val updatedProgressions = updatedSong.progressions + newProg
        updatedSong = updatedSong.copy(progressions = updatedProgressions)
        
        updateSong(updatedSong, forceUndoStep = true)
        setActiveProgression(newProg.id)
    }

    fun removeFavoriteProgression(savedProgression: songwritersnotebook.app.data.SavedProgression) {
        viewModelScope.launch {
            chordLibraryRepository?.toggleFavoriteProgression(savedProgression.progression)
            if (_uiState.value.selectedFavoriteProgression?.id == savedProgression.id) {
                _uiState.update { it.copy(selectedFavoriteProgression = null) }
            }
        }
    }

    fun playFavoriteProgression(savedProgression: songwritersnotebook.app.data.SavedProgression) {
        if (savedProgression.progression.chordNames.isNotEmpty()) {
            val firstChord = savedProgression.progression.chordNames.first()
            playChordByName(firstChord)
        }
    }
}

